<template>
  <div class="w-full">
    <PageHeader
      title="Import PrestaShop"
      subtitle="Importez et mettez en correspondance votre catalogue PrestaShop"
    />

    <div class="w-full px-3 sm:px-6 py-6 space-y-6">
      <div
        v-if="initialLoading"
        class="bg-white border border-gray-200 rounded-xl p-10 text-center"
      >
        <i class="fas fa-spinner fa-spin text-2xl text-orange-500"></i>
      </div>

      <form
        v-else-if="!connection.connected"
        class="bg-white border border-gray-200 rounded-xl p-5 sm:p-6 max-w-2xl"
        @submit.prevent="connect"
      >
        <h2 class="text-lg font-semibold text-black mb-2">
          Connecter votre boutique
        </h2>
        <p class="text-sm text-gray-600 mb-2">
          Activez le Webservice dans PrestaShop et créez une clé disposant des
          droits de lecture sur votre catalogue.
        </p>
        <button
          type="button"
          class="mb-5 inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800"
          @click="showApiKeyHelp = true"
        >
          <i class="fas fa-info-circle"></i>
          Comment récupérer ma clé API PrestaShop ?
        </button>
        <div class="space-y-4">
          <BaseInput
            v-model.trim="credentials.shopUrl"
            label="URL de la boutique"
            placeholder="https://www.ma-boutique.com"
            required
          />
          <BaseInput
            v-model="credentials.apiKey"
            type="password"
            label="Clé Webservice PrestaShop"
            placeholder="Votre clé API"
            required
          />
          <ActionButton
            type="submit"
            label="Tester et connecter"
            icon="fas fa-plug"
            variant="warning"
            :loading="connecting"
            :disabled="
              connecting || !credentials.shopUrl || !credentials.apiKey
            "
          />
        </div>
      </form>

      <template v-else>
        <div
          class="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
        >
          <div>
            <div class="flex items-center gap-2">
              <span class="w-2.5 h-2.5 rounded-full bg-green-500"></span>
              <h2 class="font-semibold text-black">Boutique connectée</h2>
            </div>
            <p class="text-sm text-gray-600 mt-1 break-all">
              {{ connection.shopUrl }}
              <span v-if="connection.prestashopVersion">
                · PrestaShop {{ connection.prestashopVersion }}
              </span>
            </p>
          </div>
          <div class="flex flex-wrap gap-2">
            <ActionButton
              label="Actualiser le catalogue"
              icon="fas fa-sync-alt"
              variant="primary"
              size="sm"
              :loading="catalogLoading"
              :disabled="catalogLoading || resettingAllOverrides"
              @click="loadCatalog"
            />
            <ActionButton
              label="Revenir aux données PrestaShop"
              icon="fas fa-undo"
              variant="secondary"
              size="sm"
              :loading="resettingAllOverrides"
              :disabled="catalogLoading || resettingAllOverrides"
              @click="showResetAllConfirm = true"
            />
            <ActionButton
              label="Configuration PrestaShop"
              icon="fas fa-cog"
              variant="secondary"
              size="sm"
              :disabled="catalogLoading || resettingAllOverrides"
              @click="showConfigurationModal = true"
            />
            <ActionButton
              label="Déconnecter"
              icon="fas fa-unlink"
              variant="secondary"
              size="sm"
              :disabled="catalogLoading || resettingAllOverrides"
              @click="disconnect"
            />
          </div>
        </div>

        <div
          v-if="
            connection.permissions?.some((permission) => !permission.readable)
          "
          class="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <p class="font-medium">
            Certaines permissions Webservice sont manquantes.
          </p>
          <ul class="mt-2 list-disc pl-5">
            <li
              v-for="permission in connection.permissions.filter(
                (item) => !item.readable
              )"
              :key="permission.resource"
            >
              {{ permission.resource
              }}<span v-if="permission.error"> — {{ permission.error }}</span>
            </li>
          </ul>
        </div>

        <div
          v-if="catalogError"
          class="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
        >
          <i class="fas fa-exclamation-circle mr-2"></i>{{ catalogError }}
        </div>

        <div
          v-if="failedSyncEvents.length"
          class="rounded-lg border border-red-200 bg-red-50 p-4"
        >
          <h3 class="text-sm font-semibold text-red-900">
            Synchronisations PrestaShop en échec
          </h3>
          <div
            v-for="event in failedSyncEvents"
            :key="event.id"
            class="mt-3 flex flex-col gap-2 border-t border-red-200 pt-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div class="text-sm text-red-800">
              <p>
                Produit PrestaShop #{{ event.prestashopProductId }} → eFashion
                #{{ event.efashionProductId }}
              </p>
              <p class="text-xs">
                {{ event.lastError || "Échec sans détail" }} ·
                {{ event.attempts }} tentative(s)
              </p>
            </div>
            <button
              type="button"
              class="rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 disabled:opacity-50"
              :disabled="retryingSyncEventId === event.id"
              @click="retrySyncEvent(event.id)"
            >
              {{ retryingSyncEventId === event.id ? "Relance…" : "Réessayer" }}
            </button>
          </div>
        </div>

        <div
          class="sticky top-[70px] z-30 bg-white/95 backdrop-blur border border-gray-200 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2 shadow-sm"
        >
          <span class="text-xs text-gray-500 shrink-0 whitespace-nowrap">
            {{ filteredProductCount }} affiché(s)
            <template v-if="selectedProductIds.length">
              · {{ selectedProductIds.length }} sélectionné(s)
            </template>
          </span>

          <form
            class="flex flex-1 items-center gap-2 min-w-[200px] max-w-lg"
            @submit.prevent="submitReferenceSearch"
          >
            <label for="prestashop-reference-search" class="sr-only">
              Rechercher une référence
            </label>
            <div class="relative flex-1 min-w-0">
              <i
                class="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]"
              ></i>
              <input
                id="prestashop-reference-search"
                v-model.trim="referenceSearch"
                type="text"
                autocomplete="off"
                placeholder="Contient la référence…"
                class="w-full h-8 pl-7 pr-7 border border-gray-300 rounded-md text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                v-if="referenceSearch || appliedSearch"
                type="button"
                aria-label="Effacer la recherche"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                @click="clearReferenceSearch"
              >
                <i class="fas fa-times text-[10px]"></i>
              </button>
            </div>
            <button
              type="submit"
              class="h-8 rounded-md bg-blue-600 px-3 text-xs font-medium text-white disabled:opacity-50"
              :disabled="catalogLoading"
            >
              Rechercher
            </button>
          </form>

          <div class="flex items-center gap-1.5 shrink-0 ml-auto">
            <ActionButton
              :label="`Modifier les fiches en masse (${selectedProductIds.length})`"
              icon="fas fa-layer-group"
              variant="primary"
              size="sm"
              :disabled="selectedProductIds.length === 0"
              @click="openBulkComplete"
            />
            <ActionButton
              :label="`Mettre en ligne (${selectedPublishableCount})`"
              icon="fas fa-cloud-upload-alt"
              variant="success"
              size="sm"
              :loading="importingProducts && !showPublishConfirm"
              :disabled="importingProducts || selectedPublishableCount === 0"
              @click="showPublishConfirm = true"
            />
          </div>
        </div>

        <section
          class="bg-white border border-gray-200 rounded-xl overflow-hidden"
        >
          <div class="p-4 sm:p-5 border-b border-gray-200">
            <h2 class="font-semibold text-black">
              {{
                appliedSearch ? "Résultats de recherche" : "Nouveaux produits"
              }}
              <span class="text-sm font-normal text-gray-500">
                ({{
                  appliedSearch
                    ? filteredNewProducts.length
                    : catalog.newProductsTotal ?? filteredNewProducts.length
                }})
              </span>
            </h2>
            <p class="text-sm text-gray-600 mt-1">
              {{
                appliedSearch
                  ? "Correspondances (contient) sur la référence, sur tout le catalogue PrestaShop."
                  : "Cochez plusieurs produits pour les compléter en masse, ou cliquez sur une ligne pour éditer une fiche."
              }}
            </p>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-[1100px] w-full text-sm">
              <thead class="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th class="px-3 py-3 w-10 sticky left-0 bg-gray-50 z-10">
                    <input
                      type="checkbox"
                      :checked="allVisibleNewSelected"
                      :disabled="filteredNewProducts.length === 0"
                      aria-label="Sélectionner tous les produits de la page"
                      class="w-4 h-4 rounded border-gray-300"
                      @change="toggleAllVisibleNewProducts"
                    />
                  </th>
                  <th class="px-3 py-3 whitespace-nowrap">État</th>
                  <th class="px-3 py-3 w-14">Photo</th>
                  <th class="px-3 py-3 whitespace-nowrap">SKU</th>
                  <th class="px-3 py-3 min-w-[180px]">Titre</th>
                  <th class="px-3 py-3 whitespace-nowrap">Marque</th>
                  <th class="px-3 py-3 whitespace-nowrap">Catégorie</th>
                  <th class="px-3 py-3 whitespace-nowrap text-right">
                    Prix HT
                  </th>
                  <th class="px-3 py-3 whitespace-nowrap text-right">Poids</th>
                  <th class="px-3 py-3 whitespace-nowrap">Vendu par</th>
                  <th class="px-3 py-3 min-w-[120px]">Couleurs</th>
                  <th class="px-3 py-3 min-w-[100px]">Tailles</th>
                  <th class="px-3 py-3 whitespace-nowrap text-center">
                    Photos
                  </th>
                  <th class="px-3 py-3 whitespace-nowrap">Statut PS</th>
                  <th class="px-3 py-3 min-w-[160px]">Manquants</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr
                  v-for="product in filteredNewProducts"
                  :key="`new-${product.id}`"
                  class="group cursor-pointer hover:bg-green-50/60"
                  :title="
                    product.readyForMel
                      ? 'Cliquer pour vérifier la fiche'
                      : 'Cliquer pour compléter la fiche'
                  "
                  @click="openProduct(product)"
                >
                  <td
                    class="px-3 py-3 sticky left-0 bg-white group-hover:bg-green-50/60"
                    @click.stop
                  >
                    <input
                      type="checkbox"
                      :checked="selectedProductIds.includes(product.id)"
                      :title="'Sélectionner ce produit'"
                      class="w-4 h-4 rounded border-gray-300"
                      @change="toggleProductSelection(product.id)"
                    />
                  </td>
                  <td class="px-3 py-3">
                    <span
                      class="inline-flex items-center gap-1.5 text-xs font-medium"
                      :class="
                        product.readyForMel ? 'text-green-700' : 'text-red-700'
                      "
                      :title="
                        product.readyForMel
                          ? 'Prêt pour la MEL'
                          : 'Fiche incomplète'
                      "
                    >
                      <span
                        class="inline-block w-2.5 h-2.5 rounded-full"
                        :class="
                          product.readyForMel ? 'bg-green-500' : 'bg-red-500'
                        "
                      ></span>
                      {{ product.readyForMel ? "Prêt" : "Incomplet" }}
                    </span>
                  </td>
                  <td class="px-3 py-3">
                    <img
                      v-if="coverUrls[product.id]"
                      :src="coverUrls[product.id]"
                      :alt="product.name || product.reference"
                      class="w-10 h-10 rounded object-cover border border-gray-200"
                    />
                    <div
                      v-else
                      class="w-10 h-10 rounded bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-400"
                    >
                      <i class="fas fa-image text-xs"></i>
                    </div>
                  </td>
                  <td
                    class="px-3 py-3 font-medium text-black whitespace-nowrap"
                  >
                    {{ product.reference || "—" }}
                  </td>
                  <td class="px-3 py-3 text-black">
                    {{ product.name || "—" }}
                  </td>
                  <td class="px-3 py-3 text-gray-700 whitespace-nowrap">
                    {{ product.brandName || "—" }}
                    <span
                      v-if="product.pendingBrands?.length"
                      class="ml-1 text-xs text-red-700"
                      >mapping à créer</span
                    >
                  </td>
                  <td class="px-3 py-3 text-gray-700">
                    {{ product.categoryName || "—" }}
                    <span
                      v-if="product.pendingCategories?.length"
                      class="ml-1 text-xs text-red-700"
                      >mapping à créer</span
                    >
                  </td>
                  <td
                    class="px-3 py-3 text-right text-gray-700 whitespace-nowrap"
                  >
                    {{ formatPrice(product.price) }}
                  </td>
                  <td
                    class="px-3 py-3 text-right text-gray-700 whitespace-nowrap"
                  >
                    {{ formatWeight(product.weight) }}
                  </td>
                  <td class="px-3 py-3 text-gray-700 whitespace-nowrap">
                    {{ product.soldByLabel || "—" }}
                  </td>
                  <td class="px-3 py-3 text-gray-700">
                    {{ product.colorsLabel || "—" }}
                  </td>
                  <td class="px-3 py-3 text-gray-700">
                    {{ product.sizesLabel || "—" }}
                  </td>
                  <td class="px-3 py-3 text-center text-gray-700">
                    {{ product.photosCount }}
                  </td>
                  <td class="px-3 py-3">
                    <span
                      class="px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap"
                      :class="
                        product.active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      "
                    >
                      {{ product.active ? "En ligne" : "Hors ligne" }}
                    </span>
                  </td>
                  <td class="px-3 py-3 text-gray-600 max-w-[220px]">
                    <span
                      v-if="!product.missingFields.length"
                      class="text-green-700"
                    >
                      Fiche complète
                    </span>
                    <span
                      v-else
                      class="line-clamp-2"
                      :title="product.missingFields.join(', ')"
                    >
                      {{ product.missingFields.join(", ") }}
                    </span>
                  </td>
                </tr>
                <tr v-if="!catalogLoading && filteredNewProducts.length === 0">
                  <td colspan="15" class="px-4 py-8 text-center text-gray-500">
                    {{
                      appliedSearch
                        ? "Aucune référence correspondante."
                        : "Aucun nouveau produit sur cette page."
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <UnifiedPagination
            v-if="paginationTotal > pageSize || catalog.hasMore"
            :total-items="paginationTotal"
            :current-page="page"
            :items-per-page="pageSize"
            :show-items-per-page="true"
            :item-label="appliedSearch ? 'résultats' : 'nouveaux produits'"
            @page-change="changePage"
            @items-per-page-change="changePageSize"
          />
        </section>
      </template>
    </div>

    <Teleport to="body">
      <div
        v-if="showApiKeyHelp"
        class="fixed inset-0 z-30 flex items-center justify-center p-3"
      >
        <button
          class="absolute inset-0 bg-black/50"
          aria-label="Fermer"
          @click="showApiKeyHelp = false"
        ></button>
        <div
          class="relative bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[70vh] overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="prestashop-api-help-title"
        >
          <div
            class="p-5 border-b border-gray-200 flex items-start justify-between gap-4"
          >
            <div>
              <h2
                id="prestashop-api-help-title"
                class="text-lg font-semibold text-black"
              >
                Récupérer votre clé API PrestaShop
              </h2>
              <p class="text-sm text-gray-600 mt-1">
                La clé Webservice est différente de votre mot de passe
                administrateur.
              </p>
            </div>
            <button
              type="button"
              class="text-gray-500 hover:text-gray-700"
              aria-label="Fermer"
              @click="showApiKeyHelp = false"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="p-5 text-sm text-gray-700 space-y-4">
            <ol class="list-decimal pl-5 space-y-2">
              <li>
                Connectez-vous au back-office de votre boutique PrestaShop.
              </li>
              <li>Ouvrez <strong>Paramètres avancés → Webservice</strong>.</li>
              <li>
                Activez le Webservice, puis cliquez sur « Ajouter une clé ».
              </li>
              <li>
                Générez une clé et donnez-lui un nom, par exemple « Connexion
                eFashion ».
              </li>
              <li>
                Accordez uniquement le droit <strong>GET</strong> aux ressources
                du catalogue : produits, déclinaisons, stocks, images,
                catégories, marques, caractéristiques, options et langues.
              </li>
              <li>
                Enregistrez puis copiez la clé dans le formulaire eFashion.
              </li>
            </ol>
            <div
              class="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800"
            >
              <i class="fas fa-shield-alt mr-2"></i>
              Ne communiquez jamais votre mot de passe administrateur
              PrestaShop.
            </div>
          </div>
          <div class="p-5 border-t border-gray-200 flex justify-end">
            <ActionButton
              label="J’ai compris"
              variant="primary"
              type="button"
              @click="showApiKeyHelp = false"
            />
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="editedProduct"
        class="fixed inset-0 z-[9998] flex items-center justify-center p-3 overflow-hidden overscroll-none"
        @click.stop
        @wheel.prevent
        @touchmove.prevent
      >
        <button
          class="absolute inset-0 bg-black/50"
          aria-label="Fermer"
          @click.stop="closeProduct"
        ></button>
        <form
          class="relative bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          @submit.prevent="saveProduct"
          @click.stop
          @wheel.stop
          @touchmove.stop
        >
          <div class="p-5 border-b border-gray-200 flex justify-between gap-4">
            <div>
              <h2 class="text-lg font-semibold text-black">
                Compléter la fiche MEL
              </h2>
              <p class="text-sm text-gray-600">
                {{ editedProduct.reference }} · {{ editedProduct.name }}
              </p>
            </div>
            <button type="button" class="text-gray-500" @click="closeProduct">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="px-5 py-4 border-b border-gray-200 space-y-3">
            <p class="text-sm font-semibold text-black">Catégorie eFashion</p>
            <div
              v-if="pendingCategories.length && !form.categoryId"
              class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 space-y-2"
            >
              <p class="text-sm font-medium text-red-800">
                Catégorie PrestaShop
              </p>
              <p class="text-xs text-red-700">
                Vous pouvez lier «
                {{ pendingCategories[0] }}
                » à une catégorie eFashion (réutilisé ensuite), ou choisir
                directement une catégorie ci-dessous
              </p>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="pending in pendingCategories"
                  :key="pending"
                  type="button"
                  class="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
                  @click="openPendingCategoryPicker(pending)"
                >
                  <i class="fas fa-link text-[10px]"></i>
                  Lier : {{ pending }}
                </button>
              </div>
            </div>
            <CategoryTreeAutocomplete
              :tree="categoriesTree"
              :cat-id="selectedCategoryLevel1"
              :sous-id="selectedCategoryLevel2"
              :sous-sous-id="selectedCategoryLevel3"
              label="Recherche rapide catégorie"
              placeholder="Rechercher une catégorie (ex. robe, jean…)"
              @select="onCategoryPathSelect"
              @clear="onCategoryPathClear"
            />
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
              <BaseInput
                v-model="selectedCategoryLevel1"
                type="select"
                open-on-focus
                label="Catégorie"
                :options="categoryLevel1Options"
                placeholder="Choisir une catégorie"
                required
                @change="onCategoryLevel1Change"
              />
              <BaseInput
                v-model="selectedCategoryLevel2"
                type="select"
                open-on-focus
                label="Sous-catégorie"
                :options="categoryLevel2Options"
                placeholder="Choisir une sous-catégorie"
                :disabled="!selectedCategoryLevel1"
                required
                @change="onCategoryLevel2Change"
              />
              <BaseInput
                v-model="selectedCategoryLevel3"
                type="select"
                open-on-focus
                label="Sous-sous-catégorie"
                :options="categoryLevel3Options"
                placeholder="Choisir une ss-catégorie"
                :disabled="
                  !selectedCategoryLevel2 || !categoryLevel3Options.length
                "
                @change="onCategoryLevel3Change"
              />
            </div>
            <p v-if="editedProduct?.categoryName" class="text-xs text-gray-500">
              Catégorie PrestaShop : {{ editedProduct.categoryName }}
            </p>
          </div>
          <div class="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <BaseInput
                v-model="form.brandId"
                type="select"
                open-on-focus
                label="Marque"
                :options="brandOptions"
                placeholder="Choisir une marque"
                :required="brandOptions.length > 1"
              />
              <p
                v-if="editedProduct?.pendingBrands?.length"
                class="text-xs text-amber-700 mt-1"
              >
                Marque PrestaShop « {{ editedProduct.pendingBrands[0] }} » à
                lier dans Configuration PrestaShop, ou choisissez une marque
                pour cette fiche.
              </p>
            </div>
            <BaseInput
              v-model="form.collectionId"
              type="select"
              open-on-focus
              label="Collection"
              :options="collectionOptions"
              placeholder="Toutes les saisons"
              required
            />
            <div>
              <BaseInput
                v-model="form.provenanceId"
                type="select"
                open-on-focus
                label="Provenance"
                :options="provenanceOptions"
                placeholder="Choisir un pays"
                required
              />
            </div>
            <div>
              <BaseInput
                v-model="form.price"
                type="number"
                label="Prix HT"
                step="0.01"
                min="0"
                placeholder="0.00"
                required
              />
              <p class="text-xs text-amber-700 mt-1">
                Les prix PrestaShop sont souvent des prix B2C. Vérifiez le prix
                grossiste HT eFashion avant la mise en ligne.
              </p>
              <p
                v-if="productPrestaPriceHint"
                class="text-xs text-gray-500 mt-1"
              >
                Prix PrestaShop d'origine :
                {{ formatPrice(productPrestaPriceHint) }}
              </p>
              <p
                v-if="productWholesalePriceHint"
                class="text-xs text-gray-500 mt-1"
              >
                Prix grossiste détecté :
                {{ formatPrice(productWholesalePriceHint) }}
              </p>
            </div>
            <div>
              <BaseInput
                v-model="form.weight"
                type="number"
                label="Poids unitaire (kg)"
                step="0.001"
                min="0.001"
                :max="5"
                placeholder="0.000"
                required
                :disabled="Boolean(editedProduct?.weightLockedByCategory)"
              />
              <p
                v-if="editedProduct?.weightLockedByCategory"
                class="text-xs text-blue-700 mt-1"
              >
                Ce poids remplace celui de PrestaShop. S’il correspond au
                paquet, il est divisé par la quantité du paquet. Au-dessus de 5
                kg à l’unité, la fiche reste bloquée.
              </p>
              <p class="text-xs text-red-600 mt-0.5">
                Merci de bien vouloir augmenter légèrement le poids pour prendre
                en compte les cintres, cartons ou surplus d'emballage. Maximum 5
                kg.
              </p>
              <div v-if="compositionType === 'chaussures'" class="mt-3">
                <BaseInput
                  v-model="form.dimensions"
                  label="Dimensions du colis (cm, facultatif)"
                />
              </div>
              <p
                v-if="productPrestaWeightHint"
                class="text-xs text-gray-500 mt-1"
              >
                Poids PrestaShop d'origine :
                {{ formatWeight(productPrestaWeightHint) }}
              </p>
            </div>
            <div class="md:col-span-2">
              <CompositionForm
                v-model="selectedCompositions"
                :composition-type="compositionType"
                :localisations="compositionLocations"
                :compositions-list="shoeCompositions"
                :autocomplete-options="compositionAutocompleteOptions"
                :show-autocomplete-dropdown="showCompositionDropdown"
                :is-loading-autocomplete="isLoadingCompositionAutocomplete"
                @composition-input="handleCompositionInput"
                @hide-dropdown="hideCompositionDropdown"
                @select-autocomplete="selectComposition"
              />
              <p
                v-if="editedProduct?.compositionLabel"
                class="mt-2 text-xs text-gray-600"
              >
                Composition PrestaShop : {{ editedProduct.compositionLabel }}
                <span
                  v-if="editedProduct.pendingCompositions?.length"
                  class="block text-amber-700 mt-1"
                >
                  Matières à valider dans Configuration PrestaShop :
                  {{ editedProduct.pendingCompositions.join(", ") }}
                </span>
              </p>
            </div>
            <BaseInput
              v-model="form.soldBy"
              type="select"
              open-on-focus
              label="Vendu par"
              :options="soldByOptions"
              placeholder="Choisir manuellement"
              required
            />
            <BaseInput
              v-model="form.sizeRangeId"
              type="select"
              open-on-focus
              label="Taille du paquet"
              :options="sizeRangeOptions"
              placeholder="Choisir une déclinaison"
              required
            />
            <BaseInput
              v-if="form.soldBy && form.soldBy !== 'tailles'"
              v-model="form.packId"
              type="select"
              open-on-focus
              label="Quantité du paquet"
              :options="packOptions"
              placeholder="Choisir un pack"
              required
            />
            <BaseInput
              v-if="form.soldBy === 'melangees' && form.sizeRangeId"
              v-model="form.stock"
              type="number"
              label="Stock en paquet (optionnel)"
              min="0"
              placeholder="0"
            />
            <div class="md:col-span-2">
              <CouleursSelector
                v-model="selectedColors"
                :couleurs-list="officialColors"
                :linked-source-labels-by-color-id="linkedLabelsWithoutCollisions"
                :tailles-list="
                  form.soldBy === 'melangees' ? selectedSizeNames : undefined
                "
              />
              <div v-if="colorCollisions.length" class="mt-3 space-y-2">
                <p class="text-sm font-medium text-red-700">
                  Plusieurs couleurs PrestaShop sont liées à la même couleur
                  officielle
                </p>
                <div
                  v-for="group in colorCollisions"
                  :key="group.colorId"
                  class="space-y-2"
                >
                  <p class="text-xs text-red-700">
                    Liées à {{ group.officialLabel }}
                  </p>
                  <div
                    v-for="name in group.names"
                    :key="`${group.colorId}-${name}`"
                    class="flex flex-wrap items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2"
                  >
                    <span class="text-sm font-semibold text-red-700">{{
                      name
                    }}</span>
                    <button
                      type="button"
                      class="text-sm text-red-700 underline hover:text-red-900"
                      @click="openCollisionColorPicker(name)"
                    >
                      {{
                        collisionChoiceLabel(name)
                          ? `Liée à ${collisionChoiceLabel(name)} pour cette fiche`
                          : "Choisir une couleur officielle"
                      }}
                    </button>
                  </div>
                </div>
              </div>
              <div v-if="pendingColors.length" class="mt-3 space-y-2">
                <p class="text-sm font-medium text-red-700">
                  Couleurs PrestaShop à lier
                </p>
                <div
                  v-for="pending in pendingColors"
                  :key="pending"
                  class="flex flex-wrap items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2"
                >
                  <span class="text-sm font-semibold text-red-700">{{
                    pending
                  }}</span>
                  <button
                    type="button"
                    class="text-sm text-red-700 underline hover:text-red-900"
                    @click="openPendingColorPicker(pending)"
                  >
                    Veuillez choisir une couleur officielle
                  </button>
                </div>
              </div>
              <div
                v-if="form.soldBy === 'melangees'"
                class="mt-2 p-3 bg-blue-50 border-l-4 border-blue-500 rounded-r"
              >
                <p v-if="!form.sizeRangeId" class="text-sm text-blue-700">
                  Sélectionnez la déclinaison pour saisir les quantités par
                  taille.
                </p>
                <p
                  v-else-if="melangeesPackMismatchMessage"
                  class="text-sm text-red-600"
                >
                  {{ melangeesPackMismatchMessage }}
                </p>
                <p v-else class="text-xs text-blue-600">
                  Pour chaque couleur, indiquez la composition du paquet par
                  taille (ex. 1 en S, 2 en M, 1 en L). La somme des couleurs
                  doit correspondre à la quantité du pack.
                </p>
              </div>
            </div>
            <div
              v-if="
                form.soldBy === 'tailles' &&
                selectedColors.length > 0 &&
                selectedSizeNames.length > 0
              "
              class="md:col-span-2 space-y-3"
            >
              <div>
                <p class="text-sm font-medium text-gray-800">
                  Stock par taille
                </p>
                <p class="text-xs text-gray-500 mt-0.5">
                  Quantités disponibles par couleur et taille (préremplies
                  depuis PrestaShop si possible).
                </p>
              </div>
              <div
                v-for="color in selectedColors"
                :key="`stock-${color.id}`"
                class="flex items-center gap-3 px-3 py-2 border border-gray-200 rounded-md bg-gray-50"
              >
                <div class="flex items-center gap-2 flex-shrink-0 w-28">
                  <span
                    class="inline-block w-3 h-3 rounded-full border border-gray-300 flex-shrink-0"
                    :style="{
                      backgroundColor: color.hexColor || '#CCCCCC',
                    }"
                  ></span>
                  <span class="text-sm font-medium text-black truncate">{{
                    color.nom
                  }}</span>
                </div>
                <div class="flex flex-wrap gap-2">
                  <div
                    v-for="(ts, idx) in color.tailleStocks || []"
                    :key="`${color.id}-${ts.taille}`"
                    class="flex items-center gap-1"
                  >
                    <span class="text-xs text-gray-600">{{ ts.taille }}</span>
                    <input
                      :value="ts.quantity != null ? String(ts.quantity) : ''"
                      type="number"
                      min="0"
                      class="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-center text-black"
                      placeholder="0"
                      @input="
                        (e) =>
                          setTailleStockQuantity(
                            color.id,
                            idx,
                            (e.target as HTMLInputElement).value
                          )
                      "
                    />
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else-if="
                form.soldBy === 'couleurs' && selectedColors.length > 0
              "
              class="md:col-span-2 space-y-3"
            >
              <div>
                <p class="text-sm font-medium text-gray-800">
                  Stock par couleur
                </p>
                <p class="text-xs text-gray-500 mt-0.5">
                  Quantités disponibles (préremplies depuis PrestaShop si
                  possible).
                </p>
              </div>
              <div
                v-for="color in selectedColors"
                :key="`color-stock-${color.id}`"
                class="p-3 border border-gray-200 rounded-md bg-gray-50 flex items-center gap-3"
              >
                <span
                  class="inline-block w-3 h-3 rounded-full border border-gray-300 flex-shrink-0"
                  :style="{ backgroundColor: color.hexColor || '#CCCCCC' }"
                ></span>
                <span class="text-sm font-medium text-black w-28 truncate">{{
                  color.nom
                }}</span>
                <input
                  :value="color.stock != null ? String(color.stock) : ''"
                  type="number"
                  min="0"
                  class="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-center text-black"
                  placeholder="0"
                  @input="
                    (e) =>
                      setColorStock(
                        color.id,
                        (e.target as HTMLInputElement).value
                      )
                  "
                />
              </div>
            </div>
          </div>
          <div class="px-5 py-4 border-t border-gray-200">
            <PrestashopMelPhotosEditor
              v-if="editedProduct"
              :key="`prestashop-photos-${editedProduct.id}`"
              :product-id="editedProduct.id"
              :source-image-ids="editedProduct.imageIds"
              :saved-images="productImageOverrides"
              :selected-colors="selectedColors"
              :linked-source-labels-by-color-id="linkedSourceLabelsByColorId"
              :source-color-by-image-id="sourceColorByImageId"
              :color-collisions="colorCollisions"
              :color-source-choices="colorSourceChoices"
              @update:images="onProductImagesUpdate"
              @saved="onProductImagesSaved"
              @error="error"
            />
          </div>
          <div class="p-5 border-t border-gray-200 space-y-3">
            <p v-if="!canSaveProductForm" class="text-sm text-red-600">
              Champs obligatoires manquants :
              {{ productFormMissingFields.join(", ") }}
            </p>
            <div class="flex justify-end gap-2">
              <ActionButton
                label="Annuler"
                variant="secondary"
                type="button"
                @click="closeProduct"
              />
              <ActionButton
                label="Enregistrer"
                variant="primary"
                type="submit"
                :loading="savingProduct"
                :disabled="savingProduct || !canSaveProductForm"
              />
            </div>
          </div>
        </form>
      </div>
    </Teleport>

    <CouleursPickerModal
      v-model:isVisible="pendingColorModalVisible"
      :couleursList="officialColors"
      :official-only="true"
      :selectedIds="collisionPickerSelectedIds"
      :single="Boolean(collisionColorToAssign)"
      :disabled-ids="collisionDisabledColorIds"
      :linked-source-labels-by-color-id="linkedLabelsWithoutCollisions"
      @confirm="onPendingColorConfirm"
    />

    <Teleport to="body">
      <div
        v-if="pendingCategoryModalVisible"
        class="fixed inset-0 z-[10050] flex items-center justify-center p-3 overflow-hidden overscroll-none"
        @click.stop
      >
        <button
          class="absolute inset-0 bg-black/50"
          aria-label="Fermer"
          @click="pendingCategoryModalVisible = false"
        ></button>
        <div
          class="relative bg-white rounded-xl shadow-xl w-full max-w-lg p-5 space-y-4"
          @click.stop
        >
          <h3 class="text-base font-semibold text-black">
            Lier la catégorie PrestaShop
          </h3>
          <p class="text-sm text-gray-600">
            «
            <span class="font-medium text-black">{{
              pendingCategoryToLink
            }}</span>
            » → catégorie feuille eFashion
          </p>
          <CategoryTreeAutocomplete
            :tree="categoriesTree"
            cat-id=""
            sous-id=""
            sous-sous-id=""
            label="Recherche catégorie"
            placeholder="Rechercher une catégorie feuille…"
            @select="onPendingCategorySelect"
          />
          <div class="flex justify-end">
            <ActionButton
              label="Annuler"
              variant="secondary"
              size="sm"
              @click="pendingCategoryModalVisible = false"
            />
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="showBulkComplete"
        class="fixed inset-0 z-[9997] flex items-center justify-center p-3 overflow-hidden overscroll-none"
        @click.stop
      >
        <button
          class="absolute inset-0 bg-black/50"
          aria-label="Fermer"
          @click.stop="closeBulkComplete"
        ></button>
        <form
          class="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          @submit.prevent="saveBulkComplete"
          @click.stop
        >
          <div
            v-if="bulkModalLoading"
            class="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/90 rounded-xl"
          >
            <i class="fas fa-spinner fa-spin text-2xl text-blue-600"></i>
            <p class="text-sm text-gray-600">Chargement du formulaire…</p>
          </div>
          <div
            v-else-if="savingBulkComplete"
            class="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/90 rounded-xl"
          >
            <i class="fas fa-spinner fa-spin text-2xl text-blue-600"></i>
            <p class="text-sm font-medium text-black">
              Application des modifications…
            </p>
            <p v-if="bulkProgressLabel" class="text-sm text-blue-700">
              {{ bulkProgressLabel }}
            </p>
            <p v-else class="text-xs text-gray-500">
              Mise à jour de {{ bulkSelectionProducts.length }} fiche(s), merci
              de patienter.
            </p>
          </div>
          <div class="p-5 border-b border-gray-200 flex justify-between gap-4">
            <div>
              <h2 class="text-lg font-semibold text-black">
                Compléter la sélection
              </h2>
              <p class="text-sm text-gray-600 mt-1">
                {{ bulkSelectionProducts.length }} produit(s) sélectionné(s).
                Seuls les champs renseignés ci-dessous seront appliqués.
                <span
                  v-if="bulkBatchCount > 1"
                  class="block mt-1 text-xs text-blue-700"
                >
                  Traitement automatique par lots de
                  {{ PRESTASHOP_BULK_BATCH_SIZE }} fiches ({{ bulkBatchCount }}
                  lots).
                </span>
              </p>
            </div>
            <button
              type="button"
              class="text-gray-500"
              :disabled="bulkModalLoading || savingBulkComplete"
              @click="closeBulkComplete"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="px-5 py-4 border-b border-gray-200 space-y-3">
            <p class="text-sm font-semibold text-black">Produits concernés</p>
            <div class="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
              <span
                v-for="product in bulkSelectionProducts"
                :key="`bulk-ref-${product.id}`"
                class="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700"
              >
                {{ product.reference || `#${product.id}` }}
              </span>
            </div>
            <div v-if="bulkMissingSummary.length" class="text-sm text-gray-600">
              <p class="font-medium text-black mb-1">Champs encore manquants</p>
              <ul class="space-y-1">
                <li v-for="[field, count] in bulkMissingSummary" :key="field">
                  {{ field }} : {{ count }}/{{ bulkSelectionProducts.length }}
                </li>
              </ul>
            </div>
          </div>

          <div class="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="md:col-span-2 space-y-3">
              <p class="text-sm font-semibold text-black">Catégorie eFashion</p>
              <CategoryTreeAutocomplete
                :tree="categoriesTree"
                :cat-id="bulkSelectedCategoryLevel1"
                :sous-id="bulkSelectedCategoryLevel2"
                :sous-sous-id="bulkSelectedCategoryLevel3"
                label="Recherche rapide catégorie"
                placeholder="Rechercher une catégorie (ex. robe, jean…)"
                @select="onBulkCategoryPathSelect"
                @clear="onBulkCategoryPathClear"
              />
              <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                <BaseInput
                  v-model="bulkSelectedCategoryLevel1"
                  type="select"
                  label="Catégorie"
                  :options="categoryLevel1Options"
                  placeholder="Ne pas modifier"
                  @change="onBulkCategoryLevel1Change"
                />
                <BaseInput
                  v-model="bulkSelectedCategoryLevel2"
                  type="select"
                  label="Sous-catégorie"
                  :options="bulkCategoryLevel2Options"
                  placeholder="Ne pas modifier"
                  :disabled="!bulkSelectedCategoryLevel1"
                  @change="onBulkCategoryLevel2Change"
                />
                <BaseInput
                  v-model="bulkSelectedCategoryLevel3"
                  type="select"
                  label="Sous-sous-catégorie"
                  :options="bulkCategoryLevel3Options"
                  placeholder="Ne pas modifier"
                  :disabled="
                    !bulkSelectedCategoryLevel2 ||
                    !bulkCategoryLevel3Options.length
                  "
                  @change="onBulkCategoryLevel3Change"
                />
              </div>
            </div>
            <BaseInput
              v-model="bulkForm.brandId"
              type="select"
              label="Marque"
              :options="brandOptions"
              placeholder="Ne pas modifier"
            />
            <BaseInput
              v-model="bulkForm.collectionId"
              type="select"
              label="Collection"
              :options="collectionOptions"
              placeholder="Ne pas modifier"
            />
            <BaseInput
              v-model="bulkForm.provenanceId"
              type="select"
              label="Provenance"
              :options="provenanceOptions"
              placeholder="Ne pas modifier"
            />
            <div>
              <BaseInput
                v-model="bulkForm.price"
                type="number"
                label="Prix HT"
                step="0.01"
                min="0"
                placeholder="Ne pas modifier"
              />
              <p class="text-xs text-amber-700 mt-1">
                Les prix PrestaShop sont souvent des prix B2C. Indiquez le prix
                grossiste HT eFashion si besoin.
              </p>
            </div>
            <div>
              <BaseInput
                v-model="bulkForm.weight"
                type="number"
                label="Poids unitaire (kg)"
                step="0.001"
                min="0.001"
                placeholder="Ne pas modifier"
              />
              <p class="text-xs text-red-600 mt-0.5">
                Merci de bien vouloir augmenter légèrement le poids pour prendre
                en compte les cintres, cartons ou surplus d'emballage.
              </p>
            </div>
            <div class="md:col-span-2">
              <CompositionForm
                v-model="bulkSelectedCompositions"
                :composition-type="bulkCompositionType"
                :localisations="bulkCompositionLocations"
                :compositions-list="bulkShoeCompositions"
                :autocomplete-options="bulkCompositionAutocompleteOptions"
                :show-autocomplete-dropdown="bulkShowCompositionDropdown"
                :is-loading-autocomplete="bulkIsLoadingCompositionAutocomplete"
                @composition-input="handleBulkCompositionInput"
                @hide-dropdown="hideBulkCompositionDropdown"
                @select-autocomplete="selectBulkComposition"
              />
            </div>
            <BaseInput
              v-model="bulkForm.soldBy"
              type="select"
              label="Vendu par"
              :options="soldByOptions"
              placeholder="Choisir manuellement"
            />
            <BaseInput
              v-model="bulkForm.sizeRangeId"
              type="select"
              label="Taille du paquet"
              :options="sizeRangeOptions"
              placeholder="Ne pas modifier"
            />
            <BaseInput
              v-if="bulkForm.soldBy && bulkForm.soldBy !== 'tailles'"
              v-model="bulkForm.packId"
              type="select"
              label="Quantité du paquet"
              :options="bulkPackOptions"
              placeholder="Ne pas modifier"
            />
            <div class="md:col-span-2">
              <CouleursSelector
                v-model="bulkSelectedColors"
                :couleurs-list="officialColors"
                :tailles-list="
                  bulkForm.soldBy === 'melangees'
                    ? bulkSelectedSizeNames
                    : undefined
                "
                :force-validate="Boolean(bulkFormValidationMessage)"
              />
              <p class="mt-2 text-xs text-gray-500">
                Optionnel : laissez vide pour ne pas modifier les couleurs des
                produits sélectionnés.
              </p>
              <div
                v-if="bulkForm.soldBy === 'melangees'"
                class="mt-2 p-3 bg-blue-50 border-l-4 border-blue-500 rounded-r"
              >
                <p v-if="!bulkForm.sizeRangeId" class="text-sm text-blue-700">
                  Sélectionnez la déclinaison pour saisir les quantités par
                  taille.
                </p>
                <p
                  v-else-if="bulkMelangeesPackMismatchMessage"
                  class="text-sm text-red-600"
                >
                  {{ bulkMelangeesPackMismatchMessage }}
                </p>
                <p v-else class="text-xs text-blue-600">
                  Pour chaque couleur, indiquez la composition du paquet par
                  taille (obligatoire, au moins une quantité &gt; 0 par
                  couleur). La somme doit correspondre au pack sélectionné.
                </p>
              </div>
            </div>
          </div>

          <div class="p-5 border-t border-gray-200 space-y-3">
            <p v-if="bulkFormValidationMessage" class="text-sm text-red-600">
              {{ bulkFormValidationMessage }}
            </p>
            <div class="flex justify-end gap-2">
              <ActionButton
                label="Annuler"
                variant="secondary"
                type="button"
                :disabled="bulkModalLoading || savingBulkComplete"
                @click="closeBulkComplete"
              />
              <ActionButton
                label="Appliquer à la sélection"
                variant="primary"
                type="submit"
                :loading="savingBulkComplete"
                :disabled="
                  bulkModalLoading || savingBulkComplete || !canSaveBulkForm
                "
              />
            </div>
          </div>
        </form>
      </div>
    </Teleport>

    <PrestashopConfigurationModal
      v-model:visible="showConfigurationModal"
      :shop-url="connection.shopUrl"
      :required-setup="!prestashopSettings.setupCompleted"
      :catalog-ready="connection.connected"
      :catalog-revision="catalogRevision"
      :pending-colors="[]"
      :pending-categories="[]"
      :pending-compositions="[]"
      :pending-brands="[]"
      @changed="loadCatalog"
      @settings-saved="onSettingsSaved"
    />

    <ConfirmModal
      :show="showResetAllConfirm"
      title="Revenir aux données PrestaShop ?"
      message="Les prix, poids, stocks et choix de photos des fiches reviendront aux données PrestaShop. Les choix de la configuration (pays, taille du paquet, quantité du paquet, correspondances) restent en place."
      confirm-text="Réinitialiser tout"
      confirm-button-class="bg-red-600 hover:bg-red-700 focus:ring-red-500"
      loading-text="Réinitialisation…"
      :confirm-loading="resettingAllOverrides"
      :show-attention-icon="true"
      :lock-background-scroll="true"
      max-width-class="max-w-lg"
      @confirm="resetAllOverrides"
      @cancel="closeResetAllConfirm"
    />

    <ConfirmModal
      :show="showPublishConfirm"
      title="Confirmer la mise en ligne"
      :attention="publishConfirmAttention"
      :message="publishConfirmMessage"
      :reminder="publishConfirmReminder"
      confirm-text="Mettre en ligne"
      loading-text="Mise en ligne…"
      :references="selectedPublishableReferences"
      :confirm-loading="importingProducts"
      :progress-label="importProgressLabel"
      :show-attention-icon="true"
      :lock-background-scroll="true"
      max-width-class="max-w-lg"
      @confirm="publishSelectedProducts"
      @cancel="closePublishConfirm"
    />

    <Teleport to="body">
      <div
        v-if="showImportResult && importResult"
        class="fixed inset-0 z-[9998] flex items-center justify-center p-3"
      >
        <button
          class="absolute inset-0 bg-black/50"
          aria-label="Fermer"
          @click="closeImportResult"
        ></button>
        <div
          class="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="prestashop-import-result-title"
        >
          <div
            class="p-5 border-b border-gray-200 flex items-start justify-between gap-4"
          >
            <div>
              <h2
                id="prestashop-import-result-title"
                class="text-lg font-semibold"
                :class="
                  importResult.tone === 'success'
                    ? 'text-green-700'
                    : importResult.tone === 'mixed'
                    ? 'text-amber-700'
                    : 'text-red-700'
                "
              >
                {{ importResult.title }}
              </h2>
              <p class="text-sm text-gray-700 mt-1">
                {{ importResult.summary }}
              </p>
            </div>
            <button
              type="button"
              class="text-gray-500"
              aria-label="Fermer"
              @click="closeImportResult"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <ul
            v-if="importResult.lines.length"
            class="px-5 py-4 space-y-2 text-sm"
          >
            <li
              v-for="(line, index) in importResult.lines"
              :key="`${line.label}-${index}`"
              class="rounded-md px-3 py-2"
              :class="
                line.success
                  ? 'bg-gray-50 text-gray-800'
                  : 'bg-red-50 text-red-800'
              "
            >
              <span class="font-medium">{{ line.label }}</span>
              <span v-if="line.detail"> — {{ line.detail }}</span>
            </li>
          </ul>
          <p
            v-if="importResult.hiddenCount"
            class="px-5 pb-2 text-xs text-gray-500"
          >
            {{ importResult.hiddenCount }} autre(s) ligne(s) non affichée(s).
          </p>
          <div class="p-5 border-t border-gray-200 flex justify-end">
            <ActionButton
              label="Fermer"
              variant="primary"
              size="sm"
              @click="closeImportResult"
            />
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { fetchRest } from "@/config/api";
import PageHeader from "@/components/layout/seller/PageHeader.vue";
import BaseInput from "@/components/forms/BaseInput.vue";
import CategoryTreeAutocomplete from "@/components/forms/CategoryTreeAutocomplete.vue";
import PrestashopMelPhotosEditor from "@/components/prestashop/PrestashopMelPhotosEditor.vue";
import CouleursSelector from "@/components/mel/CouleursSelector.vue";
import CouleursPickerModal from "@/components/mel/CouleursPickerModal.vue";
import CompositionForm from "@/components/mel/CompositionForm.vue";
import ConfirmModal from "@/components/common/ConfirmModal.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import UnifiedPagination from "@/components/ui/UnifiedPagination.vue";
import { useNotification } from "@/composables/useNotification";
import { useBodyScrollLock } from "@/composables/useBodyScrollLock";
import { getCompositionAutocomplete, getReferenceData } from "@/api/shooting";
import { getCategoriesTree } from "@/api/categories";
import { getCouleursOfficielles } from "@/api/couleurs";
import { getVendeurMarques } from "@/api/brands";
import { brandSelectOptions, resolveSellerBrandId } from "@/utils/sellerBrand";
import {
  formatDeclinaisonLabel,
  getDeclinaisonLength,
} from "@/utils/declinaisonLabel";
import { getMelUnitWeightError } from "@/utils/melCompositions";
import {
  isShoeCategory,
  isValidProductDimensions,
} from "@/utils/productDimensions";
import type { Brand } from "@/types/brand";
import PrestashopConfigurationModal from "@/components/prestashop/PrestashopConfigurationModal.vue";
import {
  connectPrestashop,
  disconnectPrestashop,
  getPrestashopCustomImageUrl,
  getPrestashopProductImage,
  getPrestashopProductVariantStocks,
  getPrestashopConnection,
  getPrestashopMappings,
  getPrestashopProducts,
  getPrestashopSettings,
  getPrestashopSyncEvents,
  retryPrestashopSyncEvent,
  savePrestashopMapping,
  importPrestashopProductsInBatches,
  toPrestashopImportRow,
  PRESTASHOP_BULK_BATCH_SIZE,
  PRESTASHOP_IMPORT_BATCH_SIZE,
  savePrestashopBulkOverridesInBatches,
  savePrestashopProductOverrides,
  resetAllPrestashopProductOverrides,
  type PrestashopImportReport,
  type PrestashopSyncEvent,
} from "@/api/prestashop";
import type {
  PrestashopCatalogPage,
  PrestashopConnection,
  PrestashopImageOverride,
  PrestashopMelOverrides,
  PrestashopProduct,
  PrestashopReferenceData,
} from "@/types/prestashop";
import type {
  CouleurAPI,
  CouleurSelectorItem,
  CompositionAutocompleteOption,
  CompositionItem,
  CompositionLocalisation,
  CompositionMateriau,
  CompositionType,
} from "@/types/mel-components";
import type { CategorieNode, CategoryPathSelection } from "@/types/categorie";

const emptyCatalog = (): PrestashopCatalogPage => ({
  items: [],
  newProducts: [],
  newProductsTotal: null,
  total: 0,
  hasMore: false,
});

const { success, error } = useNotification();
const initialLoading = ref(true);
const connecting = ref(false);
const catalogLoading = ref(false);
const resettingAllOverrides = ref(false);
const showResetAllConfirm = ref(false);
const importingProducts = ref(false);
const importProgressLabel = ref("");
const showPublishConfirm = ref(false);
const showImportResult = ref(false);
const importResult = ref<{
  title: string;
  summary: string;
  tone: "success" | "mixed" | "failure";
  lines: Array<{ label: string; detail: string; success: boolean }>;
  hiddenCount: number;
} | null>(null);
const showBulkComplete = ref(false);
const bulkModalLoading = ref(false);
const savingProduct = ref(false);
const savingBulkComplete = ref(false);
const bulkProgressLabel = ref("");
const showApiKeyHelp = ref(false);
const showConfigurationModal = ref(false);
const catalogRevision = ref(0);
const prestashopSettings = ref<import("@/api/prestashop").PrestashopSettings>({
  defaultGender: null,
  defaultProvenanceId: null,
  priceAdjustmentPercent: 0,
  priceIsUnit: true,
  weightIsUnit: true,
  defaultDeclinaisonId: null,
  defaultPackId: null,
  defaultSoldBy: null,
  setupCompleted: false,
});
const catalogError = ref("");
const syncEvents = ref<PrestashopSyncEvent[]>([]);
const retryingSyncEventId = ref<number | null>(null);
const failedSyncEvents = computed(() =>
  syncEvents.value.filter((event) => event.status === "failed")
);
const connection = ref<PrestashopConnection>({ connected: false });
const catalog = ref<PrestashopCatalogPage>(emptyCatalog());
const referenceSearch = ref("");
const appliedSearch = ref("");
const selectedProductIds = ref<number[]>([]);
const selectedProductsById = ref(new Map<number, PrestashopProduct>());
const page = ref(1);
const pageSize = ref(50);
const editedProduct = ref<PrestashopProduct | null>(null);
const productImageOverrides = ref<PrestashopImageOverride[]>([]);
const referenceData = ref<PrestashopReferenceData | null>(null);
const categoriesTree = ref<CategorieNode[]>([]);
const officialColors = ref<CouleurAPI[]>([]);
const sellerBrands = ref<Brand[]>([]);
const selectedColors = ref<CouleurSelectorItem[]>([]);
const pendingColors = computed(() => editedProduct.value?.pendingColors ?? []);
const colorCollisions = computed(
  () => editedProduct.value?.colorCollisions ?? []
);
const colorSourceChoices = ref<Array<{ sourceName: string; colorId: number }>>(
  []
);
const pendingCategories = computed(
  () => editedProduct.value?.pendingCategories ?? []
);
const productVariantStocks = ref<
  Array<{
    combinationId: number;
    quantity: number;
    optionValueIds: number[];
    colorName: string | null;
    sizeName: string | null;
    imageIds?: number[];
  }>
>([]);
const sourceColorByImageId = computed(() => {
  const colorsByImage = new Map<number, Set<string>>();
  for (const stock of productVariantStocks.value) {
    const colorName = stock.colorName?.trim();
    if (!colorName) continue;
    for (const imageId of stock.imageIds ?? []) {
      const colors = colorsByImage.get(imageId) ?? new Set<string>();
      colors.add(colorName);
      colorsByImage.set(imageId, colors);
    }
  }
  const result: Record<number, string[]> = {};
  for (const [imageId, colors] of colorsByImage) {
    result[imageId] = [...colors];
  }
  return result;
});
let syncingColorStocks = false;
const selectedCompositions = ref<CompositionItem[]>([]);
let openProductSession = 0;
let suppressPackUnitSync = false;
const compositionType = ref<CompositionType>("autres");
const compositionLocations = ref<CompositionLocalisation[]>([]);
const shoeCompositions = ref<CompositionMateriau[]>([]);
const compositionAutocompleteOptions = ref<CompositionAutocompleteOption[]>([]);
const showCompositionDropdown = ref(false);
const isLoadingCompositionAutocomplete = ref(false);
const selectedCategoryLevel1 = ref("");
const selectedCategoryLevel2 = ref("");
const selectedCategoryLevel3 = ref("");
const bulkSelectedCategoryLevel1 = ref("");
const bulkSelectedCategoryLevel2 = ref("");
const bulkSelectedCategoryLevel3 = ref("");
const bulkSelectedCompositions = ref<CompositionItem[]>([]);
const bulkCompositionType = ref<CompositionType>("autres");
const bulkCompositionLocations = ref<CompositionLocalisation[]>([]);
const bulkShoeCompositions = ref<CompositionMateriau[]>([]);
const bulkCompositionAutocompleteOptions = ref<CompositionAutocompleteOption[]>(
  []
);
const bulkShowCompositionDropdown = ref(false);
const bulkIsLoadingCompositionAutocomplete = ref(false);
const bulkSelectedColors = ref<CouleurSelectorItem[]>([]);
const isModalOpen = computed(
  () =>
    showApiKeyHelp.value ||
    showConfigurationModal.value ||
    editedProduct.value !== null ||
    showBulkComplete.value ||
    showImportResult.value
);
useBodyScrollLock(isModalOpen);
const credentials = reactive({ shopUrl: "", apiKey: "" });
const form = reactive({
  brandId: "",
  categoryId: "",
  collectionId: "",
  provenanceId: "",
  price: "",
  weight: "",
  soldBy: "",
  packId: "",
  sizeRangeId: "",
  stock: "",
  dimensions: "",
});
const bulkForm = reactive({
  brandId: "",
  categoryId: "",
  collectionId: "",
  provenanceId: "",
  price: "",
  weight: "",
  soldBy: "",
  packId: "",
  sizeRangeId: "",
});
const productPrestaPriceHint = ref<number | null>(null);
const productWholesalePriceHint = ref<number | null>(null);
const linkedSourceLabelsByColorId = ref<Record<number, string[]>>({});
const pendingColorModalVisible = ref(false);
const pendingColorToLink = ref<string | null>(null);
const collisionColorToAssign = ref<string | null>(null);
const pendingCategoryModalVisible = ref(false);
const pendingCategoryToLink = ref<string | null>(null);
const productPrestaWeightHint = ref<number | null>(null);

const paginationTotal = computed(
  () =>
    catalog.value.total ??
    (page.value - 1) * pageSize.value +
      catalog.value.items.length +
      (catalog.value.hasMore ? 1 : 0)
);
const filteredNewProducts = computed(() => {
  if (appliedSearch.value) return catalog.value.items ?? [];
  return catalog.value.newProducts ?? [];
});
const coverUrls = ref<Record<number, string>>({});
let coverLoadSequence = 0;

function revokeCoverUrls(urls: Record<number, string>) {
  Object.values(urls).forEach((url) => {
    if (url.startsWith("blob:")) URL.revokeObjectURL(url);
  });
}

async function loadCoverObjectUrl(
  product: PrestashopProduct
): Promise<string | null> {
  const cover = product.overrides?.images?.[0];
  try {
    if (cover?.url) return cover.url;
    if (cover?.type === "custom" && cover.file) {
      const response = await fetchRest(
        getPrestashopCustomImageUrl(product.id, cover.file)
      );
      if (!response.ok) return null;
      return URL.createObjectURL(await response.blob());
    }
    const savedId = Number(cover?.id);
    const imageId =
      cover?.type === "prestashop" && Number.isInteger(savedId) && savedId > 0
        ? savedId
        : product.overrides?.images?.length
        ? 0
        : product.imageIds[0];
    if (!imageId) return null;
    return URL.createObjectURL(
      await getPrestashopProductImage(product.id, imageId)
    );
  } catch {
    return null;
  }
}

watch(
  filteredNewProducts,
  async (products) => {
    const sequence = ++coverLoadSequence;
    const next: Record<number, string> = {};
    await Promise.all(
      products.map(async (product) => {
        const url = await loadCoverObjectUrl(product);
        if (url) next[product.id] = url;
      })
    );
    if (sequence !== coverLoadSequence) {
      revokeCoverUrls(next);
      return;
    }
    const previous = coverUrls.value;
    coverUrls.value = next;
    revokeCoverUrls(previous);
  },
  { immediate: true }
);

onUnmounted(() => {
  credentials.apiKey = "";
  revokeCoverUrls(coverUrls.value);
});
const filteredProductCount = computed(() => filteredNewProducts.value.length);
const bulkSelectionProducts = computed(() =>
  selectedProductIds.value
    .map(
      (productId) =>
        findCatalogProduct(productId) ??
        selectedProductsById.value.get(productId)
    )
    .filter((product): product is PrestashopProduct => Boolean(product))
);
const selectedPublishableCount = computed(
  () =>
    bulkSelectionProducts.value.filter((product) => product.readyForMel).length
);
const selectedActiveImportCount = computed(
  () =>
    bulkSelectionProducts.value.filter(
      (product) => product.readyForMel && product.active
    ).length
);
const selectedDraftImportCount = computed(
  () =>
    bulkSelectionProducts.value.filter(
      (product) => product.readyForMel && !product.active
    ).length
);
const allVisibleNewSelected = computed(
  () =>
    filteredNewProducts.value.length > 0 &&
    filteredNewProducts.value.every((product) =>
      selectedProductIds.value.includes(product.id)
    )
);
const selectedPublishableReferences = computed(() =>
  bulkSelectionProducts.value
    .filter((product) => product.readyForMel)
    .map((product) => {
      const reference = product.reference?.trim();
      const name = product.name?.trim();
      const label =
        reference && name
          ? `${reference} · ${name}`
          : reference || name || `Produit #${product.id}`;
      return {
        label,
        meta: formatPrice(productEffectivePrice(product)),
      };
    })
);
const publishBatchCount = computed(() =>
  Math.ceil(selectedPublishableCount.value / PRESTASHOP_IMPORT_BATCH_SIZE)
);
const bulkBatchCount = computed(() =>
  Math.ceil(bulkSelectionProducts.value.length / PRESTASHOP_BULK_BATCH_SIZE)
);
const publishConfirmAttention =
  "Attention : merci de bien vérifier vos fiches avant de continuer (photos, catégories, compositions, couleurs, stocks, mode de vente, prix HT grossiste, etc.).";
const publishConfirmReminder = computed(() => {
  const priceHint =
    "Rappel : les prix PrestaShop sont souvent orientés B2C ; assurez-vous que le prix grossiste HT est correct.";
  const draftCount = selectedDraftImportCount.value;
  if (!draftCount) return priceHint;
  const draftHint =
    draftCount === 1
      ? "1 produit est hors ligne sur PrestaShop : il sera importé hors ligne sur eFashion, avec toutes ses variantes, sans être mis en vente."
      : `${draftCount} produits sont hors ligne sur PrestaShop : ils seront importés hors ligne sur eFashion, avec toutes leurs variantes, sans être mis en vente.`;
  return `${priceHint}\n\n${draftHint}`;
});
const publishConfirmMessage = computed(() => {
  const batchHint =
    publishBatchCount.value > 1
      ? `\n\nL'import sera traité automatiquement par lots de ${PRESTASHOP_IMPORT_BATCH_SIZE} produits (${publishBatchCount.value} lots).`
      : "";
  const total = selectedPublishableCount.value;
  const activeCount = selectedActiveImportCount.value;
  const draftCount = selectedDraftImportCount.value;
  if (!total) {
    return `Aucun produit prêt à importer.${batchHint}`;
  }
  if (draftCount === 0) {
    return `Les ${total} produit(s) prêt(s) sélectionné(s) seront importés et publiés directement sur eFashion. Cette action est définitive pour les références concernées.${batchHint}`;
  }
  if (activeCount === 0) {
    return `Les ${total} produit(s) prêt(s) sélectionné(s) sont hors ligne sur PrestaShop : ils seront importés hors ligne sur eFashion, avec toutes leurs variantes, sans être mis en vente.${batchHint}`;
  }
  return `Les ${total} produit(s) prêt(s) sélectionné(s) seront importés sur eFashion : ${activeCount} en vente (en ligne sur PrestaShop) et ${draftCount} hors ligne, avec toutes leurs variantes (hors ligne sur PrestaShop). Cette action est définitive pour les références concernées.${batchHint}`;
});
const bulkMissingSummary = computed(() => {
  const counts = new Map<string, number>();
  for (const product of bulkSelectionProducts.value) {
    for (const field of product.missingFields) {
      counts.set(field, (counts.get(field) || 0) + 1);
    }
  }
  return Array.from(counts.entries()).sort((left, right) => right[1] - left[1]);
});
const hasBulkFormValues = computed(
  () =>
    Boolean(bulkForm.brandId) ||
    Boolean(bulkForm.categoryId) ||
    bulkSelectedCompositions.value.length > 0 ||
    bulkSelectedColors.value.length > 0 ||
    Boolean(bulkForm.collectionId) ||
    Boolean(bulkForm.provenanceId) ||
    Boolean(bulkForm.price) ||
    Boolean(bulkForm.weight) ||
    Boolean(bulkForm.soldBy) ||
    Boolean(bulkForm.packId) ||
    Boolean(bulkForm.sizeRangeId)
);

const option = (id: string | number, label: string) => ({
  value: String(id),
  label,
});
const brandOptions = computed(() => brandSelectOptions(sellerBrands.value));
const collectionOptions = computed(() =>
  (referenceData.value?.collections || []).map((item) =>
    option(item.id, item.label || String(item.id))
  )
);
const provenanceOptions = computed(() =>
  (referenceData.value?.provenances || []).map((item) =>
    option(item.id, item.label || String(item.id))
  )
);
const packOptions = computed(() => packOptionsForSizeRange(form.sizeRangeId));
const bulkPackOptions = computed(() =>
  packOptionsForSizeRange(bulkForm.sizeRangeId)
);
const bulkSelectedSizeNames = computed(() => {
  const range = (referenceData.value?.declinaisons || []).find(
    (item) => String(item.id) === bulkForm.sizeRangeId
  );
  return String(range?.tailles || "")
    .split(",")
    .map((size) => size.trim())
    .filter(Boolean);
});
const sizeRangeOptions = computed(() =>
  (referenceData.value?.declinaisons || []).map((item) =>
    option(
      item.id,
      formatDeclinaisonLabel(item.label, item.tailles) || String(item.id)
    )
  )
);
const categoryLevel1Options = computed(() =>
  categoriesTree.value.map((item) =>
    option(item.id_categorie, item.label || String(item.id_categorie))
  )
);
const categoryLevel2Options = computed(() => {
  if (!selectedCategoryLevel1.value) return [];
  const level1 = categoriesTree.value.find(
    (item) => String(item.id_categorie) === selectedCategoryLevel1.value
  );
  return (level1?.children || []).map((item) =>
    option(item.id_categorie, item.label || String(item.id_categorie))
  );
});
const categoryLevel3Options = computed(() => {
  if (!selectedCategoryLevel1.value || !selectedCategoryLevel2.value) return [];
  const level1 = categoriesTree.value.find(
    (item) => String(item.id_categorie) === selectedCategoryLevel1.value
  );
  const level2 = level1?.children?.find(
    (item) => String(item.id_categorie) === selectedCategoryLevel2.value
  );
  return (level2?.children || []).map((item) =>
    option(item.id_categorie, item.label || String(item.id_categorie))
  );
});
const bulkCategoryLevel2Options = computed(() => {
  if (!bulkSelectedCategoryLevel1.value) return [];
  const level1 = categoriesTree.value.find(
    (item) => String(item.id_categorie) === bulkSelectedCategoryLevel1.value
  );
  return (level1?.children || []).map((item) =>
    option(item.id_categorie, item.label || String(item.id_categorie))
  );
});
const bulkCategoryLevel3Options = computed(() => {
  if (!bulkSelectedCategoryLevel1.value || !bulkSelectedCategoryLevel2.value) {
    return [];
  }
  const level1 = categoriesTree.value.find(
    (item) => String(item.id_categorie) === bulkSelectedCategoryLevel1.value
  );
  const level2 = level1?.children?.find(
    (item) => String(item.id_categorie) === bulkSelectedCategoryLevel2.value
  );
  return (level2?.children || []).map((item) =>
    option(item.id_categorie, item.label || String(item.id_categorie))
  );
});

function countCsvParts(value: string | number | undefined | null) {
  return String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean).length;
}

function packOptionsForSizeRange(sizeRangeId: string) {
  if (!sizeRangeId) return [];
  const selectedDeclinaison = (referenceData.value?.declinaisons || []).find(
    (item) => String(item.id) === String(sizeRangeId)
  );
  const tailleCount = getDeclinaisonLength(selectedDeclinaison?.tailles);
  if (!tailleCount) return [];
  return (referenceData.value?.packs || [])
    .filter((pack) => countCsvParts(pack.value) === tailleCount)
    .map((item) =>
      option(
        item.id,
        item.value
          ? `${item.label || item.id} (${item.value})`
          : item.label || String(item.id)
      )
    );
}
const selectedSizeNames = computed(() => {
  const range = (referenceData.value?.declinaisons || []).find(
    (item) => String(item.id) === form.sizeRangeId
  );
  return String(range?.tailles || "")
    .split(",")
    .map((size) => size.trim())
    .filter(Boolean);
});

function normalizeStockLabel(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function matchesStockColor(
  stockColorName: string | null,
  colorName: string
): boolean {
  if (!stockColorName) return selectedColors.value.length === 1;
  return normalizeStockLabel(stockColorName) === normalizeStockLabel(colorName);
}

function quantityForColorSize(
  colorName: string,
  sizeName: string
): number | null {
  const sizeKey = normalizeStockLabel(sizeName);
  let total = 0;
  let found = false;
  for (const stock of productVariantStocks.value) {
    if (!matchesStockColor(stock.colorName, colorName)) continue;
    const stockSizeKey = stock.sizeName
      ? normalizeStockLabel(stock.sizeName)
      : "tu";
    if (stockSizeKey !== sizeKey && !(sizeKey === "tu" && !stock.sizeName)) {
      continue;
    }
    found = true;
    total += stock.quantity;
  }
  return found ? total : null;
}

function quantityForColor(colorName: string): number | null {
  let total = 0;
  let found = false;
  for (const stock of productVariantStocks.value) {
    if (!matchesStockColor(stock.colorName, colorName)) continue;
    found = true;
    total += stock.quantity;
  }
  return found ? total : null;
}

function syncMelangeesPackStock(preferExisting = true) {
  if (form.soldBy !== "melangees") return;
  if (preferExisting && form.stock !== "") return;
  if (!productVariantStocks.value.length) return;
  form.stock = String(
    productVariantStocks.value.reduce(
      (total, stock) => total + stock.quantity,
      0
    )
  );
}

function syncColorStocks(preferExisting = true) {
  if (syncingColorStocks) return;
  syncingColorStocks = true;
  try {
    if (selectedColors.value.length) {
      selectedColors.value = selectedColors.value.map((color) => {
        if (form.soldBy === "tailles") {
          const sizes = selectedSizeNames.value;
          if (!sizes.length) {
            return { ...color, tailleStocks: undefined, stock: undefined };
          }
          const existingBySize = new Map(
            (color.tailleStocks || []).map((item) => [
              normalizeStockLabel(item.taille),
              item.quantity,
            ])
          );
          return {
            ...color,
            stock: undefined,
            tailleStocks: sizes.map((taille) => {
              const key = normalizeStockLabel(taille);
              const existing = existingBySize.get(key);
              if (preferExisting && existing != null) {
                return { taille, quantity: existing };
              }
              const fromPrestashop = quantityForColorSize(color.nom, taille);
              if (fromPrestashop != null) {
                return { taille, quantity: fromPrestashop };
              }
              return {
                taille,
                quantity: existing !== undefined ? existing : null,
              };
            }),
          };
        }
        if (form.soldBy === "couleurs") {
          const fromPrestashop = quantityForColor(color.nom);
          return {
            ...color,
            tailleStocks: undefined,
            stock:
              preferExisting && color.stock != null
                ? color.stock
                : fromPrestashop != null
                ? fromPrestashop
                : color.stock ?? null,
          };
        }
        return { ...color, tailleStocks: undefined, stock: undefined };
      });
    }
    syncMelangeesPackStock(preferExisting);
  } finally {
    syncingColorStocks = false;
  }
}

function setTailleStockQuantity(
  colorId: number,
  index: number,
  rawValue: string
) {
  selectedColors.value = selectedColors.value.map((color) => {
    if (color.id !== colorId || !color.tailleStocks?.[index]) return color;
    const next = color.tailleStocks.map((item, itemIndex) =>
      itemIndex === index
        ? {
            ...item,
            quantity:
              rawValue === "" ? null : Math.max(0, Number(rawValue) || 0),
          }
        : item
    );
    return { ...color, tailleStocks: next };
  });
}

function setColorStock(colorId: number, rawValue: string) {
  selectedColors.value = selectedColors.value.map((color) =>
    color.id === colorId
      ? {
          ...color,
          stock: rawValue === "" ? null : Math.max(0, Number(rawValue) || 0),
        }
      : color
  );
}

async function loadProductVariantStocks(productId: number) {
  try {
    const response = await getPrestashopProductVariantStocks(productId);
    if (editedProduct.value?.id !== productId) return;
    productVariantStocks.value = response.stocks || [];
    syncColorStocks(true);
  } catch {
    if (editedProduct.value?.id === productId) {
      productVariantStocks.value = [];
      syncColorStocks(true);
    }
  }
}

const soldByOptions = [
  option("tailles", "Tailles aux choix"),
  option("couleurs", "À la couleur"),
  option("melangees", "Couleurs mélangées"),
];
function normalizeCompositionsForSave(
  compositions: CompositionItem[],
  type: CompositionType
) {
  if (type !== "chaussures") {
    return compositions.map((composition) => ({
      compositionId: composition.compositionId,
      locationId: composition.localisationId,
      percentage: Number(composition.percentage) || 0,
    }));
  }
  const countByLocation = new Map<number, number>();
  for (const composition of compositions) {
    countByLocation.set(
      composition.localisationId,
      (countByLocation.get(composition.localisationId) || 0) + 1
    );
  }
  return compositions.map((composition) => ({
    compositionId: composition.compositionId,
    locationId: composition.localisationId,
    percentage: 100 / (countByLocation.get(composition.localisationId) || 1),
  }));
}

const normalizedCompositionsForSave = computed(() =>
  normalizeCompositionsForSave(
    selectedCompositions.value,
    compositionType.value
  )
);
const normalizedBulkCompositionsForSave = computed(() =>
  normalizeCompositionsForSave(
    bulkSelectedCompositions.value,
    bulkCompositionType.value
  )
);

function validateCompositionsForSave(
  compositions: Array<{
    compositionId: number;
    locationId: number;
    percentage: number;
  }>,
  type: CompositionType,
  locations: CompositionLocalisation[]
) {
  const totalsByLocation = new Map<number, number>();
  for (const composition of compositions) {
    totalsByLocation.set(
      composition.locationId,
      (totalsByLocation.get(composition.locationId) || 0) +
        composition.percentage
    );
  }
  const valid =
    compositions.length > 0 &&
    compositions.every((composition) => composition.percentage > 0) &&
    Array.from(totalsByLocation.values()).every(
      (total) => Math.abs(total - 100) <= 0.01
    ) &&
    (type !== "chaussures" ||
      locations.every((location) =>
        compositions.some(
          (composition) =>
            composition.locationId === location.id_composition_localisation
        )
      ));
  return {
    valid,
    message:
      type === "chaussures"
        ? "Sélectionnez une composition pour chaque zone de la chaussure."
        : "Chaque emplacement de composition doit totaliser exactement 100 %.",
  };
}

function parsePackQuantities(value: string | number | undefined | null) {
  return String(value || "")
    .split(/[,-]/)
    .map((part) => Number.parseInt(part.trim(), 10))
    .filter((quantity) => !Number.isNaN(quantity));
}

function selectedPackQuantityTotal(packId: string): number {
  const selectedPack = (referenceData.value?.packs || []).find(
    (pack) => String(pack.id) === String(packId)
  );
  const total = parsePackQuantities(selectedPack?.value).reduce(
    (sum, quantity) => sum + Math.max(0, quantity),
    0
  );
  return total > 1 ? total : 0;
}

function applyPackUnitValues() {
  const product = editedProduct.value;
  const total = selectedPackQuantityTotal(form.packId);
  if (!product || !total) return;
  const settings = prestashopSettings.value;
  if (!settings.priceIsUnit) {
    const rawPrice = Number(product.prestashopPrice) || 0;
    if (rawPrice > 0) {
      const rate = (Number(settings.priceAdjustmentPercent) || 0) / 100;
      const factor = rate > 0 && rate < 1 ? 1 / (1 - rate) : 1;
      form.price = String(Math.round((rawPrice / total) * factor * 100) / 100);
    }
  }
  if (!settings.weightIsUnit) {
    const rawWeight = product.weightLockedByCategory
      ? Number(product.overrides?.weight) || 0
      : Number(product.prestashopWeight ?? product.weight) || 0;
    if (rawWeight > 0) {
      form.weight = String(Math.ceil((rawWeight / total) * 10 - 1e-9) / 10);
    }
  }
}

function mixedPackMismatchMessage(
  packId: string,
  colors: CouleurSelectorItem[]
): string {
  if (!packId || !colors.length) return "";
  const selectedPack = (referenceData.value?.packs || []).find(
    (pack) => String(pack.id) === String(packId)
  );
  const packQuantites = parsePackQuantities(selectedPack?.value);
  if (!packQuantites.length) return "";

  const sommeQuantites = new Array(packQuantites.length).fill(0);
  for (const color of colors) {
    (color.quantitesParTaille || []).forEach((quantity, index) => {
      if (index < sommeQuantites.length) {
        sommeQuantites[index] += Number(quantity) || 0;
      }
    });
  }

  for (let index = 0; index < packQuantites.length; index += 1) {
    if (packQuantites[index] !== sommeQuantites[index]) {
      return `La somme des quantités des couleurs (${sommeQuantites.join(
        "-"
      )}) ne correspond pas aux quantités du paquet (${packQuantites.join(
        "-"
      )})`;
    }
  }
  return "";
}

const melangeesPackMismatchMessage = computed(() =>
  form.soldBy === "melangees"
    ? mixedPackMismatchMessage(form.packId, selectedColors.value)
    : ""
);
const bulkMelangeesPackMismatchMessage = computed(() => {
  return bulkForm.soldBy === "melangees"
    ? mixedPackMismatchMessage(bulkForm.packId, bulkSelectedColors.value)
    : "";
});

function parsePositivePrice(value: string): number | undefined {
  const parsed = Number(String(value).trim().replace(",", "."));
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return Math.round(parsed * 100) / 100;
}

function parsePositiveWeight(value: string): number | undefined {
  const parsed = Number(String(value).trim().replace(",", "."));
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return Math.round(parsed * 1000) / 1000;
}

function syncProductPrestaPriceHint() {
  const product = editedProduct.value;
  if (!product) {
    productPrestaPriceHint.value = null;
    productWholesalePriceHint.value = null;
    productPrestaWeightHint.value = null;
    return;
  }
  const prestashopPrice = product.prestashopPrice ?? 0;
  const wholesalePrice = product.wholesalePrice ?? 0;
  const currentPrice = parsePositivePrice(form.price);
  productPrestaPriceHint.value =
    prestashopPrice > 0 &&
    currentPrice !== undefined &&
    Math.abs(currentPrice - prestashopPrice) > 0.001
      ? prestashopPrice
      : null;
  productWholesalePriceHint.value = wholesalePrice > 0 ? wholesalePrice : null;
  const prestashopWeight = Number(product.weight) || 0;
  const currentWeight = parsePositiveWeight(form.weight);
  productPrestaWeightHint.value =
    prestashopWeight > 0 &&
    currentWeight !== undefined &&
    Math.abs(currentWeight - prestashopWeight) > 0.0005
      ? prestashopWeight
      : null;
}

function validateBulkOverridesBeforeSave(): string | null {
  if (bulkForm.price.trim() && !parsePositivePrice(bulkForm.price)) {
    return "Le prix doit être supérieur à 0.";
  }
  if (bulkForm.weight.trim() && !parsePositiveWeight(bulkForm.weight)) {
    return "Le poids doit être supérieur à 0.";
  }
  const soldBy = bulkForm.soldBy;
  const hasColors = bulkSelectedColors.value.length > 0;
  const appliesSoldBy =
    soldBy === "couleurs" || soldBy === "melangees" || soldBy === "tailles";

  if (appliesSoldBy && soldBy !== "tailles") {
    if (!bulkForm.sizeRangeId) {
      return "Sélectionnez une déclinaison pour ce mode de vente.";
    }
    if (!bulkForm.packId) {
      return "Sélectionnez un pack pour ce mode de vente.";
    }
  }

  if (soldBy === "melangees" && hasColors) {
    if (!bulkForm.sizeRangeId) {
      return "Sélectionnez une déclinaison pour saisir les quantités mélangées.";
    }
    const missingQuantities = bulkSelectedColors.value.some(
      (color) =>
        !color.quantitesParTaille?.some((quantity) => Number(quantity) > 0)
    );
    if (missingQuantities) {
      return "Renseignez les quantités par taille pour chaque couleur (au moins une quantité > 0 par couleur).";
    }
    if (bulkMelangeesPackMismatchMessage.value) {
      return bulkMelangeesPackMismatchMessage.value;
    }
  }

  const hasMelangeesQuantities = bulkSelectedColors.value.some((color) =>
    color.quantitesParTaille?.some((quantity) => Number(quantity) > 0)
  );
  if (hasMelangeesQuantities && soldBy !== "melangees") {
    return "Les quantités par taille ne s'appliquent qu'au mode couleurs mélangées.";
  }

  return null;
}

const bulkSoldByValidationMessage = computed(() => {
  if (!showBulkComplete.value) return "";
  return validateBulkOverridesBeforeSave() || "";
});

const bulkCompositionValidationMessage = computed(() => {
  if (!showBulkComplete.value || !bulkSelectedCompositions.value.length) {
    return "";
  }
  const { valid, message } = validateCompositionsForSave(
    normalizedBulkCompositionsForSave.value,
    bulkCompositionType.value,
    bulkCompositionLocations.value
  );
  return valid ? "" : message;
});

const bulkFormValidationMessage = computed(
  () =>
    bulkSoldByValidationMessage.value || bulkCompositionValidationMessage.value
);

const canSaveBulkForm = computed(
  () => hasBulkFormValues.value && !bulkFormValidationMessage.value
);

const productFormMissingFields = computed(() => {
  const product = editedProduct.value;
  if (!product) return [];
  const missing: string[] = [];
  if (!product.reference?.trim()) missing.push("SKU");
  if (!parsePositivePrice(form.price)) missing.push("Prix");
  const weightError = getMelUnitWeightError(form.weight);
  if (weightError) missing.push(weightError.replace(/\.$/, ""));
  if (!productImageOverrides.value.length) {
    missing.push("Photo (obligatoire pour la mise en ligne)");
  } else if (selectedColors.value.length) {
    const normalize = (value: string | null | undefined) =>
      value?.trim().toLocaleLowerCase("fr") ?? "";
    const colorsWithoutPhoto = selectedColors.value
      .filter((color) => {
        return !productImageOverrides.value.some((image) => {
          if (image.shared) return true;
          if (color.id && Number(image.colorId) === Number(color.id))
            return true;
          if (
            color.id &&
            image.colorIds?.some((id) => Number(id) === Number(color.id))
          )
            return true;
          if (
            [image.colorName, ...(image.colorNames ?? [])].some(
              (name) =>
                Boolean(name?.trim()) &&
                normalize(name) === normalize(color.nom)
            )
          ) {
            return true;
          }
          const linkedNames =
            sourceColorByImageId.value[Number(image.id)] || [];
          if (
            linkedNames.some(
              (name) => normalize(name) === normalize(color.nom)
            )
          ) {
            return true;
          }
          const sourceName =
            image.colorName || linkedNames.find((name) => name?.trim()) || "";
          return colorSourceChoices.value.some(
            (choice) =>
              Number(choice.colorId) === Number(color.id) &&
              collisionLabelKey(choice.sourceName) ===
                collisionLabelKey(sourceName)
          );
        });
      })
      .map((color) => color.nom);
    if (colorsWithoutPhoto.length) {
      missing.push(`Photo manquante pour : ${colorsWithoutPhoto.join(", ")}`);
    }
  }
  const pendingCategoriesList = product.pendingCategories ?? [];
  if (!form.categoryId) {
    missing.push(
      pendingCategoriesList.length
        ? "Catégorie eFashion (lier ou choisir ci-dessous)"
        : "Catégorie eFashion"
    );
  }
  if (!form.brandId) missing.push("Marque");
  if (!form.collectionId) missing.push("Collection");
  if (!form.provenanceId) missing.push("Provenance");

  const compositions = normalizedCompositionsForSave.value;
  const totalsByLocation = new Map<number, number>();
  for (const composition of compositions) {
    totalsByLocation.set(
      composition.locationId,
      (totalsByLocation.get(composition.locationId) || 0) +
        composition.percentage
    );
  }
  const compositionsValid =
    compositions.length > 0 &&
    compositions.every((composition) => composition.percentage > 0) &&
    Array.from(totalsByLocation.values()).every(
      (total) => Math.abs(total - 100) <= 0.01
    ) &&
    (compositionType.value !== "chaussures" ||
      compositionLocations.value.every((location) =>
        compositions.some(
          (composition) =>
            composition.locationId === location.id_composition_localisation
        )
      ));
  if (!compositions.length) {
    missing.push("Composition");
  } else if (!compositionsValid) {
    missing.push("Pourcentages de composition");
  }
  if (
    compositionType.value === "chaussures" &&
    form.dimensions.trim() &&
    !isValidProductDimensions(form.dimensions)
  ) {
    missing.push("Dimensions du colis");
  }

  if (!selectedColors.value.length) missing.push("Couleur");
  const pendingColors = product.pendingColors ?? [];
  if (pendingColors.length) {
    missing.push(`Couleurs à lier : ${pendingColors.join(", ")}`);
  }
  const collisionMessage = collisionBlockMessage();
  if (collisionMessage) missing.push(collisionMessage);
  const soldBy =
    form.soldBy === "couleurs" ||
    form.soldBy === "melangees" ||
    form.soldBy === "tailles"
      ? form.soldBy
      : "couleurs";
  if (!form.sizeRangeId) missing.push("Déclinaison");
  if (soldBy !== "tailles" && !form.packId) missing.push("Pack");
  if (
    form.soldBy === "melangees" &&
    (!selectedColors.value.length ||
      selectedColors.value.some(
        (color) =>
          !color.quantitesParTaille?.some((quantity) => Number(quantity) > 0)
      ))
  ) {
    missing.push("Quantités des couleurs mélangées");
  }
  if (melangeesPackMismatchMessage.value) {
    missing.push("Somme des quantités ≠ pack");
  }
  return missing;
});

const canSaveProductForm = computed(
  () => productFormMissingFields.value.length === 0
);

async function connect() {
  connecting.value = true;
  try {
    connection.value = await connectPrestashop(
      credentials.shopUrl,
      credentials.apiKey
    );
    credentials.apiKey = "";
    success("Boutique PrestaShop connectée.");
    prestashopSettings.value = await getPrestashopSettings();
    showConfigurationModal.value = true;
    if (prestashopSettings.value.setupCompleted) await loadCatalog();
  } catch (caught) {
    credentials.apiKey = "";
    error(caught instanceof Error ? caught.message : "Connexion impossible.");
  } finally {
    connecting.value = false;
  }
}

function closeResetAllConfirm() {
  if (resettingAllOverrides.value) return;
  showResetAllConfirm.value = false;
}

async function resetAllOverrides() {
  if (resettingAllOverrides.value || catalogLoading.value) return;
  resettingAllOverrides.value = true;
  try {
    const report = await resetAllPrestashopProductOverrides();
    editedProduct.value = null;
    selectedProductIds.value = [];
    selectedProductsById.value = new Map();
    await loadCatalog();
    success(
      report.cleared > 0
        ? `${report.cleared} fiche(s) remise(s) aux données PrestaShop d’origine.`
        : "Aucune modification à effacer : le catalogue est déjà à l’état PrestaShop."
    );
    showResetAllConfirm.value = false;
  } catch (caught) {
    error(
      caught instanceof Error
        ? caught.message
        : "Impossible de réinitialiser les fiches PrestaShop."
    );
  } finally {
    resettingAllOverrides.value = false;
  }
}

async function disconnect() {
  if (!window.confirm("Déconnecter cette boutique PrestaShop ?")) return;
  try {
    await disconnectPrestashop();
    connection.value = { connected: false };
    catalog.value = emptyCatalog();
    referenceSearch.value = "";
    appliedSearch.value = "";
    success("Boutique PrestaShop déconnectée.");
  } catch (caught) {
    error(caught instanceof Error ? caught.message : "Déconnexion impossible.");
  }
}

function formatPrice(value: number) {
  if (!(value > 0)) return "—";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(value);
}

function productEffectivePrice(product: PrestashopProduct): number {
  const override = product.overrides?.price;
  if (typeof override === "number" && override > 0) return override;
  return product.price;
}

function productEffectiveWeight(product: PrestashopProduct): number {
  const override = product.overrides?.weight;
  if (typeof override === "number" && override > 0) return override;
  return product.weight;
}

function formatWeight(value: number) {
  if (!(value > 0)) return "—";
  return `${value.toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} kg`;
}

function findCatalogProduct(productId: number): PrestashopProduct | undefined {
  return (
    catalog.value.newProducts.find((product) => product.id === productId) ??
    catalog.value.items.find((product) => product.id === productId)
  );
}

function cacheSelectedProduct(product: PrestashopProduct) {
  const next = new Map(selectedProductsById.value);
  next.set(product.id, product);
  selectedProductsById.value = next;
}

function uncacheSelectedProduct(productId: number) {
  if (!selectedProductsById.value.has(productId)) return;
  const next = new Map(selectedProductsById.value);
  next.delete(productId);
  selectedProductsById.value = next;
}

function refreshSelectedProductsCache(products: PrestashopProduct[]) {
  if (!selectedProductIds.value.length) return;
  const next = new Map(selectedProductsById.value);
  for (const product of products) {
    if (selectedProductIds.value.includes(product.id)) {
      next.set(product.id, product);
    }
  }
  selectedProductsById.value = next;
}

function patchCatalogOverridesForProducts(
  productIds: number[],
  patch: Partial<PrestashopMelOverrides>
) {
  if (!productIds.length || !Object.keys(patch).length) return;
  const idSet = new Set(productIds);
  const updateList = (products: PrestashopProduct[]) =>
    products.map((product) => {
      if (!idSet.has(product.id)) return product;
      const overrides = { ...product.overrides, ...patch };
      const price =
        typeof patch.price === "number" && patch.price > 0
          ? patch.price
          : productEffectivePrice({ ...product, overrides });
      const weight =
        typeof patch.weight === "number" && patch.weight > 0
          ? patch.weight
          : productEffectiveWeight({ ...product, overrides });
      return { ...product, overrides, price, weight };
    });
  catalog.value = {
    ...catalog.value,
    items: updateList(catalog.value.items),
    newProducts: updateList(catalog.value.newProducts),
  };
  refreshSelectedProductsCache(catalog.value.newProducts);
}

function removeImportedSelection(productIds: number[]) {
  if (!productIds.length) return;
  const imported = new Set(productIds);
  selectedProductIds.value = selectedProductIds.value.filter(
    (id) => !imported.has(id)
  );
  const next = new Map(selectedProductsById.value);
  for (const productId of productIds) next.delete(productId);
  selectedProductsById.value = next;
}

const locallyMarkedImportIds = new Set<number>();

function markImportedCatalogRows(
  results: Array<{
    prestashopProductId: number;
    success: boolean;
    efashionProductId?: number;
  }>
) {
  const fresh = results.filter(
    (result) =>
      result.success && !locallyMarkedImportIds.has(result.prestashopProductId)
  );
  if (!fresh.length) return;
  for (const result of fresh) {
    locallyMarkedImportIds.add(result.prestashopProductId);
  }
  const imported = new Set(fresh.map((result) => result.prestashopProductId));
  const removedFromNew = catalog.value.newProducts.filter((product) =>
    imported.has(product.id)
  ).length;
  const previousTotal = catalog.value.newProductsTotal;
  catalog.value = {
    ...catalog.value,
    items: catalog.value.items.filter((product) => !imported.has(product.id)),
    newProducts: catalog.value.newProducts.filter(
      (product) => !imported.has(product.id)
    ),
    newProductsTotal:
      previousTotal == null
        ? previousTotal
        : Math.max(0, previousTotal - removedFromNew),
  };
}

function toggleProductSelection(productId: number) {
  if (selectedProductIds.value.includes(productId)) {
    selectedProductIds.value = selectedProductIds.value.filter(
      (id) => id !== productId
    );
    uncacheSelectedProduct(productId);
    return;
  }
  const product = findCatalogProduct(productId);
  if (product) cacheSelectedProduct(product);
  selectedProductIds.value = [...selectedProductIds.value, productId];
}

function toggleAllVisibleNewProducts() {
  const visibleProducts = filteredNewProducts.value;
  const visibleIds = visibleProducts.map((product) => product.id);
  if (allVisibleNewSelected.value) {
    selectedProductIds.value = selectedProductIds.value.filter(
      (id) => !visibleIds.includes(id)
    );
    for (const productId of visibleIds) uncacheSelectedProduct(productId);
    return;
  }
  for (const product of visibleProducts) cacheSelectedProduct(product);
  selectedProductIds.value = Array.from(
    new Set([...selectedProductIds.value, ...visibleIds])
  );
}

async function preloadBulkReferences() {
  try {
    const [references, tree, colors, brands] = await Promise.all([
      referenceData.value
        ? Promise.resolve(referenceData.value)
        : getReferenceData(),
      categoriesTree.value.length
        ? Promise.resolve(categoriesTree.value)
        : getCategoriesTree("fr"),
      officialColors.value.length
        ? Promise.resolve(officialColors.value)
        : getCouleursOfficielles(),
      sellerBrands.value.length
        ? Promise.resolve(sellerBrands.value)
        : getVendeurMarques(),
    ]);
    referenceData.value = references;
    categoriesTree.value = tree;
    officialColors.value = colors;
    sellerBrands.value = brands;
  } catch {
    // Le chargement sera retenté à l'ouverture de la modal.
  }
}

function resetBulkForm() {
  bulkForm.brandId = "";
  bulkForm.categoryId = "";
  bulkForm.collectionId = "";
  bulkForm.provenanceId = "";
  bulkForm.price = "";
  bulkForm.weight = "";
  bulkForm.soldBy = "";
  bulkForm.packId = "";
  bulkForm.sizeRangeId = "";
  bulkSelectedCategoryLevel1.value = "";
  bulkSelectedCategoryLevel2.value = "";
  bulkSelectedCategoryLevel3.value = "";
  bulkSelectedCompositions.value = [];
  bulkCompositionAutocompleteOptions.value = [];
  bulkShowCompositionDropdown.value = false;
  bulkSelectedColors.value = [];
  configureBulkCompositionReferences();
}

async function openBulkComplete() {
  if (!selectedProductIds.value.length) return;
  resetBulkForm();
  showBulkComplete.value = true;
  bulkModalLoading.value = true;
  try {
    await preloadBulkReferences();
    configureBulkCompositionReferences();
  } catch (caught) {
    showBulkComplete.value = false;
    error(
      caught instanceof Error
        ? caught.message
        : "Référentiels eFashion indisponibles."
    );
  } finally {
    bulkModalLoading.value = false;
  }
}

function closeBulkComplete() {
  if (bulkModalLoading.value || savingBulkComplete.value) return;
  showBulkComplete.value = false;
}

async function saveBulkComplete() {
  if (!selectedProductIds.value.length || !canSaveBulkForm.value) return;

  const bulkCompositions = bulkSelectedCompositions.value.length
    ? normalizedBulkCompositionsForSave.value
    : [];
  if (bulkSelectedCompositions.value.length) {
    const { valid, message } = validateCompositionsForSave(
      bulkCompositions,
      bulkCompositionType.value,
      bulkCompositionLocations.value
    );
    if (!valid) {
      error(message);
      return;
    }
  }
  const bulkValidationMessage = validateBulkOverridesBeforeSave();
  if (bulkValidationMessage) {
    error(bulkValidationMessage);
    return;
  }

  savingBulkComplete.value = true;
  bulkProgressLabel.value =
    selectedProductIds.value.length > PRESTASHOP_BULK_BATCH_SIZE
      ? `Préparation (${selectedProductIds.value.length} fiches)…`
      : "";
  try {
    const overrides: PrestashopMelOverrides = {};
    if (bulkForm.brandId) {
      overrides.brandId = Number(bulkForm.brandId);
    }
    if (bulkForm.categoryId) {
      overrides.categoryId = Number(bulkForm.categoryId);
    }
    if (bulkForm.collectionId) {
      overrides.collectionId = Number(bulkForm.collectionId);
    }
    if (bulkForm.provenanceId) {
      overrides.provenanceId = Number(bulkForm.provenanceId);
    }
    const bulkPrice = parsePositivePrice(bulkForm.price);
    if (bulkPrice) {
      overrides.price = bulkPrice;
    }
    const bulkWeight = parsePositiveWeight(bulkForm.weight);
    if (bulkWeight) {
      overrides.weight = bulkWeight;
    }
    if (bulkCompositions.length) {
      overrides.compositionId = bulkCompositions[0]?.compositionId;
      overrides.compositionLocationId = bulkCompositions[0]?.locationId;
      overrides.compositionPercentage =
        Number(bulkCompositions[0]?.percentage) || undefined;
      overrides.compositions = bulkCompositions;
    }
    if (
      bulkForm.soldBy === "couleurs" ||
      bulkForm.soldBy === "melangees" ||
      bulkForm.soldBy === "tailles"
    ) {
      overrides.soldBy = bulkForm.soldBy;
    }
    if (bulkForm.packId) overrides.packId = Number(bulkForm.packId);
    if (bulkForm.sizeRangeId) {
      overrides.sizeRangeId = Number(bulkForm.sizeRangeId);
    }
    if (bulkSelectedColors.value.length) {
      overrides.colorId = bulkSelectedColors.value[0]?.id;
      overrides.colorIds = bulkSelectedColors.value.map((color) => color.id);
      overrides.colors = bulkSelectedColors.value.map((color) => ({
        colorId: color.id,
        isMain: color.isMain,
        quantitiesBySize: color.quantitesParTaille,
        stock:
          color.stock != null
            ? Math.max(0, Number(color.stock) || 0)
            : undefined,
      }));
    }

    const report = await savePrestashopBulkOverridesInBatches(
      selectedProductIds.value,
      overrides,
      {
        onBatchProgress: ({
          batchIndex,
          batchCount,
          updatedSoFar,
          failedSoFar,
        }) => {
          bulkProgressLabel.value =
            batchCount > 1
              ? `Lot ${batchIndex}/${batchCount} — ${updatedSoFar} mise(s) à jour, ${failedSoFar} échec(s)`
              : "";
        },
      }
    );
    const catalogPatch: Partial<PrestashopMelOverrides> = {};
    if (bulkPrice) catalogPatch.price = bulkPrice;
    if (bulkWeight) catalogPatch.weight = bulkWeight;
    if (Object.keys(catalogPatch).length) {
      patchCatalogOverridesForProducts(selectedProductIds.value, catalogPatch);
    }
    await loadCatalog();
    showBulkComplete.value = false;
    if (report.updated > 0) {
      success(
        `${report.updated} fiche(s) complétée(s)${
          report.failed ? `, ${report.failed} en échec` : ""
        }.`
      );
    }
    if (report.failed > 0) {
      const messages = report.results
        .filter((result) => !result.success)
        .map(
          (result) =>
            `#${result.prestashopProductId} : ${result.message || "échec"}`
        )
        .join("\n");
      error(messages);
    }
  } catch (caught) {
    error(caught instanceof Error ? caught.message : "Sauvegarde impossible.");
  } finally {
    savingBulkComplete.value = false;
    bulkProgressLabel.value = "";
  }
}

async function publishSelectedProducts() {
  const publishableRows = bulkSelectionProducts.value
    .filter((product) => product.readyForMel)
    .map((product) => toPrestashopImportRow(product));
  if (!publishableRows.length || importingProducts.value) return;
  const labels = new Map(
    publishableRows.map((row) => [
      row.id,
      row.name?.trim() || row.reference?.trim() || `Produit #${row.id}`,
    ])
  );
  importingProducts.value = true;
  importProgressLabel.value =
    publishableRows.length > PRESTASHOP_IMPORT_BATCH_SIZE
      ? `Préparation de l'import (${publishableRows.length} produits)…`
      : "Mise en ligne en cours…";
  try {
    const report = await importPrestashopProductsInBatches(publishableRows, {
      onBatchProgress: ({
        batchIndex,
        batchCount,
        importedSoFar,
        failedSoFar,
        results,
      }) => {
        importProgressLabel.value =
          batchCount > 1
            ? `Lot ${batchIndex}/${batchCount} — ${importedSoFar} importé(s), ${failedSoFar} échec(s)`
            : "Mise en ligne en cours…";
        markImportedCatalogRows(results);
        removeImportedSelection(
          results
            .filter((result) => result.success)
            .map((result) => result.prestashopProductId)
        );
      },
    });
    showPublishConfirm.value = false;
    openImportResult(report, labels);
    clearProductSelection();
    await loadCatalog();
  } catch (caught) {
    showPublishConfirm.value = false;
    openImportFailure(
      caught instanceof Error ? caught.message : "Import impossible."
    );
    clearProductSelection();
    await loadCatalog();
  } finally {
    importingProducts.value = false;
    importProgressLabel.value = "";
  }
}

function clearProductSelection() {
  selectedProductIds.value = [];
  selectedProductsById.value = new Map();
}

function openImportResult(
  report: PrestashopImportReport,
  labels: Map<number, string>
) {
  const onlineCount = report.results.filter(
    (result) =>
      result.success &&
      (result.visibility
        ? result.visibility === "online"
        : Boolean(result.published))
  ).length;
  const draftCount = report.results.filter(
    (result) => result.success && result.visibility === "draft"
  ).length;
  const offlineCount = report.results.filter(
    (result) =>
      result.success &&
      result.visibility !== "draft" &&
      (result.visibility ? result.visibility === "offline" : !result.published)
  ).length;
  const parts: string[] = [];
  if (onlineCount) parts.push(`${onlineCount} en vente`);
  if (offlineCount) parts.push(`${offlineCount} hors ligne`);
  if (draftCount) parts.push(`${draftCount} en brouillon`);
  let summary = "";
  if (report.imported > 0) {
    summary = `${report.imported} produit(s) importé(s)`;
    if (parts.length) summary += ` : ${parts.join(", ")}`;
    summary += ".";
    if (report.failed) summary += ` ${report.failed} en échec.`;
  } else {
    summary = `${report.failed} produit(s) en échec.`;
  }
  const lines = report.results.flatMap((result) => {
    const label =
      labels.get(result.prestashopProductId) ||
      `Produit #${result.prestashopProductId}`;
    if (!result.success) {
      return [
        {
          label,
          detail: result.message || "Échec",
          success: false,
        },
      ];
    }
    if (!result.message) return [];
    return [{ label, detail: result.message, success: true }];
  });
  importResult.value = {
    title:
      report.imported > 0
        ? report.failed > 0
          ? "Import terminé"
          : "Import réussi"
        : "Import en échec",
    summary,
    tone:
      report.imported > 0 && report.failed === 0
        ? "success"
        : report.imported > 0
        ? "mixed"
        : "failure",
    lines: lines.slice(0, 8),
    hiddenCount: Math.max(0, lines.length - 8),
  };
  showImportResult.value = true;
}

function openImportFailure(message: string) {
  importResult.value = {
    title: "Import impossible",
    summary: message,
    tone: "failure",
    lines: [],
    hiddenCount: 0,
  };
  showImportResult.value = true;
}

function closeImportResult() {
  showImportResult.value = false;
}

function closePublishConfirm() {
  if (importingProducts.value) return;
  showPublishConfirm.value = false;
}

let catalogRequestSequence = 0;
async function loadCatalog() {
  if (!connection.value.connected) return;
  const sequence = ++catalogRequestSequence;
  catalogLoading.value = true;
  catalogError.value = "";
  try {
    let nextCatalog = await getPrestashopProducts(
      page.value,
      pageSize.value,
      appliedSearch.value || undefined
    );
    if (sequence !== catalogRequestSequence) return;
    const visibleCount = appliedSearch.value
      ? nextCatalog.items.length
      : (nextCatalog.newProducts?.length ?? 0);
    if (!appliedSearch.value && page.value > 1 && visibleCount === 0) {
      const total = nextCatalog.newProductsTotal ?? nextCatalog.total ?? 0;
      const lastPage = Math.max(1, Math.ceil(total / pageSize.value));
      if (lastPage !== page.value) {
        page.value = lastPage;
        nextCatalog = await getPrestashopProducts(
          page.value,
          pageSize.value,
          appliedSearch.value || undefined,
        );
        if (sequence !== catalogRequestSequence) return;
      }
    }
    catalog.value = nextCatalog;
    locallyMarkedImportIds.clear();
    catalogRevision.value += 1;
    refreshSelectedProductsCache(catalog.value.items);
    void preloadBulkReferences();
    void loadSyncEvents();
  } catch (caught) {
    if (sequence !== catalogRequestSequence) return;
    catalogError.value =
      caught instanceof Error ? caught.message : "Catalogue indisponible.";
  } finally {
    if (sequence === catalogRequestSequence) catalogLoading.value = false;
  }
}

async function loadSyncEvents() {
  if (!connection.value.connected) return;
  try {
    syncEvents.value = await getPrestashopSyncEvents();
  } catch {
    // Le catalogue reste utilisable si l'état asynchrone est momentanément indisponible.
  }
}

async function retrySyncEvent(eventId: number) {
  if (retryingSyncEventId.value) return;
  retryingSyncEventId.value = eventId;
  try {
    await retryPrestashopSyncEvent(eventId);
    await loadSyncEvents();
    success("Synchronisation remise en attente.");
  } catch (caught) {
    error(
      caught instanceof Error
        ? caught.message
        : "Nouvelle tentative impossible."
    );
  } finally {
    retryingSyncEventId.value = null;
  }
}

async function submitReferenceSearch() {
  const search = referenceSearch.value.trim();
  if (search === appliedSearch.value) return;
  appliedSearch.value = search;
  page.value = 1;
  await loadCatalog();
}

async function clearReferenceSearch() {
  referenceSearch.value = "";
  if (!appliedSearch.value) return;
  appliedSearch.value = "";
  page.value = 1;
  await loadCatalog();
}

async function openProduct(product: PrestashopProduct) {
  const session = ++openProductSession;
  editedProduct.value = product;
  selectedCompositions.value = [];
  selectedColors.value = [];
  productPrestaPriceHint.value = null;
  productWholesalePriceHint.value = null;
  productPrestaWeightHint.value = null;
  pendingColorModalVisible.value = false;
  pendingColorToLink.value = null;
  collisionColorToAssign.value = null;
  colorSourceChoices.value = (
    product.overrides?.colorSourceChoices ?? []
  ).map((choice) => ({
    sourceName: choice.sourceName,
    colorId: Number(choice.colorId),
  }));
  pendingCategoryModalVisible.value = false;
  pendingCategoryToLink.value = null;
  void loadColorMappings();
  productImageOverrides.value = product.overrides?.images?.length
    ? [...product.overrides.images]
    : product.imageIds.map((id) => ({
        type: "prestashop" as const,
        id,
        rotation: 0,
      }));
  productVariantStocks.value = [];
  void loadProductVariantStocks(product.id);
  const values = product.overrides || {};
  form.brandId = "";
  form.categoryId = values.categoryId ? String(values.categoryId) : "";
  form.collectionId = values.collectionId ? String(values.collectionId) : "";
  form.provenanceId = values.provenanceId ? String(values.provenanceId) : "";
  const effectivePrice =
    product.price > 0
      ? product.price
      : values.price && values.price > 0
      ? values.price
      : product.prestashopPrice ?? 0;
  form.price = effectivePrice > 0 ? String(effectivePrice) : "";
  const effectiveWeight = product.weightLockedByCategory
    ? product.weight
    : values.weight && values.weight > 0
    ? values.weight
    : product.weight > 0
    ? product.weight
    : 0;
  form.weight = effectiveWeight > 0 ? String(effectiveWeight) : "";
  form.dimensions = values.dimensions?.trim() || "";
  syncProductPrestaPriceHint();
  form.soldBy =
    values.soldBy === "couleurs" ||
    values.soldBy === "melangees" ||
    values.soldBy === "tailles"
      ? values.soldBy
      : "couleurs";
  suppressPackUnitSync = true;
  form.packId = values.packId ? String(values.packId) : "";
  suppressPackUnitSync = false;
  form.sizeRangeId = values.sizeRangeId ? String(values.sizeRangeId) : "";
  form.stock =
    values.stock !== undefined && values.stock !== null
      ? String(values.stock)
      : "";
  try {
    const [references, tree, colors, brands] = await Promise.all([
      referenceData.value
        ? Promise.resolve(referenceData.value)
        : getReferenceData(),
      categoriesTree.value.length
        ? Promise.resolve(categoriesTree.value)
        : getCategoriesTree("fr"),
      officialColors.value.length
        ? Promise.resolve(officialColors.value)
        : getCouleursOfficielles(),
      sellerBrands.value.length
        ? Promise.resolve(sellerBrands.value)
        : getVendeurMarques(),
    ]);
    if (session !== openProductSession) return;
    if (editedProduct.value?.id !== product.id) return;
    referenceData.value = references;
    categoriesTree.value = tree;
    officialColors.value = colors;
    sellerBrands.value = brands;
    form.brandId = resolveSellerBrandId({
      brandId: values.brandId,
      brandName: product.brandName,
      brands,
    });
    applyConfiguredDefaults(references);
    restoreCategoryPath(form.categoryId);
    configureCompositionReferences();
    const compositions = values.compositions?.length
      ? values.compositions
      : values.compositionId &&
        values.compositionLocationId &&
        values.compositionPercentage
      ? [
          {
            compositionId: values.compositionId,
            locationId: values.compositionLocationId,
            percentage: values.compositionPercentage,
          },
        ]
      : [];
    if (selectedCompositions.value.length === 0) {
      selectedCompositions.value = compositions.map((composition) => {
        const reference = (referenceData.value?.compositions || []).find(
          (item) =>
            item.type === "composition" &&
            Number(item.id) === Number(composition.compositionId)
        );
        return {
          compositionId: Number(composition.compositionId),
          localisationId: Number(composition.locationId),
          compositionName:
            reference?.libelle ||
            reference?.label ||
            `Composition ${composition.compositionId}`,
          percentage: Number(composition.percentage),
        };
      });
    }
    const selectedIds = values.colorIds?.length
      ? values.colorIds
      : values.colorId
      ? [values.colorId]
      : [];
    const savedColors: NonNullable<PrestashopMelOverrides["colors"]> = values
      .colors?.length
      ? values.colors
      : (selectedIds.map((colorId, index) => ({
          colorId,
          isMain: index === 0,
        })) as NonNullable<PrestashopMelOverrides["colors"]>);
    if (selectedColors.value.length === 0) {
      selectedColors.value = savedColors.flatMap((savedColor, index) => {
        const color = officialColors.value.find(
          (item) => Number(item.id_couleur) === Number(savedColor.colorId)
        );
        const sizeStocks = savedColor.sizeStocks;
        return color
          ? [
              {
                id: Number(color.id_couleur),
                nom: color.couleur_FR,
                isMain: savedColor.isMain || index === 0,
                hexColor: color.hexColor || null,
                quantitesParTaille: savedColor.quantitiesBySize,
                tailleStocks: sizeStocks?.map((item) => ({
                  taille: item.size,
                  quantity: item.quantity,
                })),
                stock:
                  savedColor.stock !== undefined && savedColor.stock !== null
                    ? savedColor.stock
                    : null,
              },
            ]
          : [];
      });
    }
    syncColorStocks(true);
  } catch (caught) {
    error(
      caught instanceof Error
        ? caught.message
        : "Référentiels eFashion indisponibles."
    );
  }
}

function findCategoryPath(
  nodes: CategorieNode[],
  targetId: string,
  path: string[] = []
): string[] | null {
  for (const node of nodes) {
    const nextPath = [...path, String(node.id_categorie)];
    if (String(node.id_categorie) === targetId) return nextPath;
    const childPath = findCategoryPath(node.children || [], targetId, nextPath);
    if (childPath) return childPath;
  }
  return null;
}

function applyConfiguredDefaults(references: PrestashopReferenceData) {
  if (!form.provenanceId && prestashopSettings.value.defaultProvenanceId) {
    const configuredId = String(prestashopSettings.value.defaultProvenanceId);
    if (
      references.provenances.some((item) => String(item.id) === configuredId)
    ) {
      form.provenanceId = configuredId;
    }
  }

  if (!form.sizeRangeId && prestashopSettings.value.defaultDeclinaisonId) {
    const configuredId = String(prestashopSettings.value.defaultDeclinaisonId);
    if (
      references.declinaisons.some((item) => String(item.id) === configuredId)
    ) {
      form.sizeRangeId = configuredId;
    }
  }

  if (!form.packId && prestashopSettings.value.defaultPackId) {
    const configuredId = String(prestashopSettings.value.defaultPackId);
    if (
      packOptionsForSizeRange(form.sizeRangeId).some(
        (item) => String(item.value) === configuredId
      )
    ) {
      form.packId = configuredId;
    }
  }
}

function restoreCategoryPath(categoryId: string) {
  const path = categoryId
    ? findCategoryPath(categoriesTree.value, categoryId)
    : null;
  selectedCategoryLevel1.value = path?.[0] || "";
  selectedCategoryLevel2.value = path?.[1] || "";
  selectedCategoryLevel3.value = path?.[2] || "";
}

function compositionReferencesForCategory(categoryId: string) {
  const shoeCategory = isShoeCategory(categoryId);
  const categoryFilter = shoeCategory ? "14" : null;
  const references = referenceData.value?.compositions || [];
  const locations = references
    .filter(
      (item) =>
        item.type === "localisation" &&
        (item.id_categorie ?? null) === categoryFilter
    )
    .map((item) => ({
      id_composition_localisation: Number(item.id),
      libelle: item.libelle || item.label || String(item.id),
    }));
  const materials = shoeCategory
    ? references
        .filter(
          (item) =>
            item.type === "composition" &&
            String(item.id_categorie || "") === "14"
        )
        .map((item) => ({
          id_composition: Number(item.id),
          libelle: item.libelle || item.label || String(item.id),
        }))
    : [];
  return {
    type: (shoeCategory ? "chaussures" : "autres") as CompositionType,
    locations,
    materials,
  };
}

function configureCompositionReferences(clearSelection = false) {
  const references = compositionReferencesForCategory(
    selectedCategoryLevel1.value || form.categoryId
  );
  compositionType.value = references.type;
  compositionLocations.value = references.locations;
  shoeCompositions.value = references.materials;
  if (clearSelection) selectedCompositions.value = [];
}

function syncBulkMelangeesColors() {
  if (bulkForm.soldBy !== "melangees") return;
  const sizes = bulkSelectedSizeNames.value;
  if (!sizes.length || !bulkSelectedColors.value.length) return;
  bulkSelectedColors.value = bulkSelectedColors.value.map((color) => ({
    ...color,
    quantitesParTaille: sizes.map(
      (_, index) => Number(color.quantitesParTaille?.[index]) || 0
    ),
  }));
}

function configureBulkCompositionReferences(clearSelection = false) {
  const references = compositionReferencesForCategory(
    bulkSelectedCategoryLevel1.value || bulkForm.categoryId
  );
  bulkCompositionType.value = references.type;
  bulkCompositionLocations.value = references.locations;
  bulkShoeCompositions.value = references.materials;
  if (clearSelection) bulkSelectedCompositions.value = [];
}

function onCategoryPathSelect(selection: CategoryPathSelection) {
  selectedCategoryLevel1.value = selection.catId;
  selectedCategoryLevel2.value = selection.sousId;
  selectedCategoryLevel3.value = selection.sousSousId;
  form.categoryId = selection.leafId;
  configureCompositionReferences(true);
}

function syncCategoryIdFromLevels() {
  if (selectedCategoryLevel3.value) {
    form.categoryId = selectedCategoryLevel3.value;
    return;
  }
  if (selectedCategoryLevel2.value) {
    form.categoryId = categoryLevel3Options.value.length
      ? ""
      : selectedCategoryLevel2.value;
    return;
  }
  if (selectedCategoryLevel1.value) {
    form.categoryId = categoryLevel2Options.value.length
      ? ""
      : selectedCategoryLevel1.value;
    return;
  }
  form.categoryId = "";
}

function onCategoryLevel1Change() {
  selectedCategoryLevel2.value = "";
  selectedCategoryLevel3.value = "";
  syncCategoryIdFromLevels();
  configureCompositionReferences(true);
}

function onCategoryLevel2Change() {
  selectedCategoryLevel3.value = "";
  syncCategoryIdFromLevels();
  configureCompositionReferences(true);
}

function onCategoryLevel3Change() {
  syncCategoryIdFromLevels();
  configureCompositionReferences(true);
}

function onCategoryPathClear() {
  selectedCategoryLevel1.value = "";
  selectedCategoryLevel2.value = "";
  selectedCategoryLevel3.value = "";
  form.categoryId = "";
  configureCompositionReferences(true);
}

function onBulkCategoryPathSelect(selection: CategoryPathSelection) {
  bulkSelectedCategoryLevel1.value = selection.catId;
  bulkSelectedCategoryLevel2.value = selection.sousId;
  bulkSelectedCategoryLevel3.value = selection.sousSousId;
  bulkForm.categoryId = selection.leafId;
  configureBulkCompositionReferences(true);
}

function syncBulkCategoryIdFromLevels() {
  if (bulkSelectedCategoryLevel3.value) {
    bulkForm.categoryId = bulkSelectedCategoryLevel3.value;
    return;
  }
  if (bulkSelectedCategoryLevel2.value) {
    bulkForm.categoryId = bulkCategoryLevel3Options.value.length
      ? ""
      : bulkSelectedCategoryLevel2.value;
    return;
  }
  if (bulkSelectedCategoryLevel1.value) {
    bulkForm.categoryId = bulkCategoryLevel2Options.value.length
      ? ""
      : bulkSelectedCategoryLevel1.value;
    return;
  }
  bulkForm.categoryId = "";
}

function onBulkCategoryLevel1Change() {
  bulkSelectedCategoryLevel2.value = "";
  bulkSelectedCategoryLevel3.value = "";
  syncBulkCategoryIdFromLevels();
  configureBulkCompositionReferences(true);
}

function onBulkCategoryLevel2Change() {
  bulkSelectedCategoryLevel3.value = "";
  syncBulkCategoryIdFromLevels();
  configureBulkCompositionReferences(true);
}

function onBulkCategoryLevel3Change() {
  syncBulkCategoryIdFromLevels();
  configureBulkCompositionReferences(true);
}

function onBulkCategoryPathClear() {
  bulkSelectedCategoryLevel1.value = "";
  bulkSelectedCategoryLevel2.value = "";
  bulkSelectedCategoryLevel3.value = "";
  bulkForm.categoryId = "";
  configureBulkCompositionReferences(true);
}

async function handleBulkCompositionInput(
  value: string,
  localisationId: string | null
) {
  const term = value.trim();
  if (!term) {
    bulkCompositionAutocompleteOptions.value = [];
    bulkShowCompositionDropdown.value = false;
    return;
  }
  try {
    bulkIsLoadingCompositionAutocomplete.value = true;
    const selectedIds = bulkSelectedCompositions.value
      .filter((composition) =>
        localisationId
          ? composition.localisationId === Number(localisationId)
          : true
      )
      .map((composition) => composition.compositionId)
      .join("#");
    bulkCompositionAutocompleteOptions.value = await getCompositionAutocomplete(
      term,
      selectedIds
    );
    bulkShowCompositionDropdown.value =
      bulkCompositionAutocompleteOptions.value.length > 0;
  } catch {
    bulkCompositionAutocompleteOptions.value = [];
    bulkShowCompositionDropdown.value = false;
  } finally {
    bulkIsLoadingCompositionAutocomplete.value = false;
  }
}

function selectBulkComposition() {
  bulkCompositionAutocompleteOptions.value = [];
  bulkShowCompositionDropdown.value = false;
}

function hideBulkCompositionDropdown() {
  window.setTimeout(() => {
    bulkShowCompositionDropdown.value = false;
  }, 200);
}

async function handleCompositionInput(
  value: string,
  localisationId: string | null
) {
  const term = value.trim();
  if (!term) {
    compositionAutocompleteOptions.value = [];
    showCompositionDropdown.value = false;
    return;
  }
  try {
    isLoadingCompositionAutocomplete.value = true;
    const selectedIds = selectedCompositions.value
      .filter((composition) =>
        localisationId
          ? composition.localisationId === Number(localisationId)
          : true
      )
      .map((composition) => composition.compositionId)
      .join("#");
    compositionAutocompleteOptions.value = await getCompositionAutocomplete(
      term,
      selectedIds
    );
    showCompositionDropdown.value =
      compositionAutocompleteOptions.value.length > 0;
  } catch {
    compositionAutocompleteOptions.value = [];
    showCompositionDropdown.value = false;
  } finally {
    isLoadingCompositionAutocomplete.value = false;
  }
}

function selectComposition() {
  compositionAutocompleteOptions.value = [];
  showCompositionDropdown.value = false;
}

function hideCompositionDropdown() {
  window.setTimeout(() => {
    showCompositionDropdown.value = false;
  }, 200);
}

function syncCatalogProductImages(
  productId: number,
  images: PrestashopImageOverride[]
) {
  const patchProduct = (product: PrestashopProduct): PrestashopProduct =>
    product.id === productId
      ? {
          ...product,
          overrides: {
            ...(product.overrides || {}),
            images,
          },
          photosCount: images.length,
        }
      : product;

  catalog.value = {
    ...catalog.value,
    items: catalog.value.items.map(patchProduct),
    newProducts: catalog.value.newProducts.map(patchProduct),
  };
  const selected = selectedProductsById.value.get(productId);
  if (selected) cacheSelectedProduct(patchProduct(selected));
}

function onProductImagesUpdate(images: PrestashopImageOverride[]) {
  productImageOverrides.value = images;
  if (!editedProduct.value) return;
  editedProduct.value = {
    ...editedProduct.value,
    overrides: {
      ...(editedProduct.value.overrides || {}),
      images,
    },
    photosCount: images.length,
  };
  syncCatalogProductImages(editedProduct.value.id, images);
}

function onProductImagesSaved(images: PrestashopImageOverride[]) {
  onProductImagesUpdate(images);
}

function patchEditedCatalogProduct(patch: Partial<PrestashopProduct>) {
  if (!editedProduct.value) return;
  const productId = editedProduct.value.id;
  editedProduct.value = { ...editedProduct.value, ...patch };
  const updateList = (products: PrestashopProduct[]) =>
    products.map((product) =>
      product.id === productId ? { ...product, ...patch } : product
    );
  catalog.value = {
    ...catalog.value,
    items: updateList(catalog.value.items),
    newProducts: updateList(catalog.value.newProducts),
  };
}

async function loadColorMappings() {
  try {
    const mappings = await getPrestashopMappings("color");
    const grouped: Record<number, string[]> = {};
    for (const mapping of mappings) {
      const id = Number(mapping.targetId);
      if (!Number.isInteger(id) || id <= 0) continue;
      grouped[id] = [...(grouped[id] ?? []), mapping.sourceLabel];
    }
    linkedSourceLabelsByColorId.value = grouped;
  } catch {
    linkedSourceLabelsByColorId.value = {};
  }
}

function collisionLabelKey(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const linkedLabelsWithoutCollisions = computed(() => {
  const blocked = new Set(
    colorCollisions.value.flatMap((group) =>
      group.names.map((name) => collisionLabelKey(name))
    )
  );
  const next: Record<number, string[]> = {};
  for (const [id, labels] of Object.entries(linkedSourceLabelsByColorId.value)) {
    const kept = labels.filter(
      (label) => !blocked.has(collisionLabelKey(label))
    );
    if (kept.length) next[Number(id)] = kept;
  }
  return next;
});

function collisionChoice(sourceName: string) {
  const key = collisionLabelKey(sourceName);
  return colorSourceChoices.value.find(
    (choice) => collisionLabelKey(choice.sourceName) === key
  );
}

function collisionChoiceLabel(sourceName: string) {
  const choice = collisionChoice(sourceName);
  if (!choice) return "";
  return (
    officialColors.value.find(
      (color) => Number(color.id_couleur) === choice.colorId
    )?.couleur_FR || ""
  );
}

function collisionBlockMessage(): string | null {
  const groups = colorCollisions.value;
  if (!groups.length) return null;
  const buckets = new Map<number, string[]>();
  const withoutChoice: string[] = [];
  for (const group of groups) {
    for (const name of group.names) {
      const choice = collisionChoice(name);
      if (!choice) {
        withoutChoice.push(name);
        const current = buckets.get(group.colorId) ?? [];
        current.push(name);
        buckets.set(group.colorId, current);
        continue;
      }
      const current = buckets.get(choice.colorId) ?? [];
      current.push(name);
      buckets.set(choice.colorId, current);
    }
  }
  if (withoutChoice.length) {
    return `Couleurs à séparer : choisissez une couleur officielle pour ${withoutChoice.join(", ")}.`;
  }
  const shared = [...buckets.values()].filter((names) => names.length > 1);
  if (!shared.length) return null;
  return `Couleurs à séparer : ${shared
    .map((names) => names.join(", "))
    .join(" ; ")}.`;
}

function dropAbandonedMappedColors() {
  selectedColors.value = selectedColors.value.filter((color) => {
    const labels = linkedSourceLabelsByColorId.value[Number(color.id)] ?? [];
    if (labels.length < 2) return true;
    const allMoved = labels.every((label) => {
      const choice = collisionChoice(label);
      return choice && Number(choice.colorId) !== Number(color.id);
    });
    return !allMoved;
  });
  if (
    selectedColors.value.length &&
    !selectedColors.value.some((color) => color.isMain)
  ) {
    selectedColors.value = selectedColors.value.map((color, index) => ({
      ...color,
      isMain: index === 0,
    }));
  }
}

function assignCollisionColor(sourceName: string, official: CouleurAPI) {
  const colorId = Number(official.id_couleur);
  if (!Number.isInteger(colorId) || colorId <= 0) {
    error("Couleur officielle invalide.");
    return;
  }
  const key = collisionLabelKey(sourceName);
  colorSourceChoices.value = [
    ...colorSourceChoices.value.filter(
      (choice) => collisionLabelKey(choice.sourceName) !== key
    ),
    { sourceName, colorId },
  ];
  const groups = colorCollisions.value;
  const collapsedIds = new Set(groups.map((group) => group.colorId));
  const neededIds = new Set<number>();
  for (const group of groups) {
    for (const name of group.names) {
      const choice = collisionChoice(name);
      neededIds.add(choice?.colorId ?? group.colorId);
    }
  }
  selectedColors.value = selectedColors.value.filter(
    (color) =>
      !collapsedIds.has(Number(color.id)) || neededIds.has(Number(color.id))
  );
  for (const neededId of neededIds) {
    if (selectedColors.value.some((color) => Number(color.id) === neededId)) {
      continue;
    }
    const known = officialColors.value.find(
      (color) => Number(color.id_couleur) === neededId
    );
    const group = groups.find((item) => item.colorId === neededId);
    selectedColors.value = [
      ...selectedColors.value,
      {
        id: neededId,
        nom: known?.couleur_FR || group?.officialLabel || String(neededId),
        isMain: selectedColors.value.length === 0,
        hexColor: known?.hexColor || official.hexColor || null,
        quantitesParTaille:
          form.soldBy === "melangees" && selectedSizeNames.value.length
            ? new Array(selectedSizeNames.value.length).fill(0)
            : undefined,
      },
    ];
  }
  if (
    selectedColors.value.length &&
    !selectedColors.value.some((color) => color.isMain)
  ) {
    selectedColors.value = selectedColors.value.map((color, index) => ({
      ...color,
      isMain: index === 0,
    }));
  }
  dropAbandonedMappedColors();
  syncColorStocks(true);
}

const collisionPickerSelectedIds = computed(() => {
  const name = collisionColorToAssign.value;
  if (!name) return [];
  const choice = collisionChoice(name);
  return choice ? [choice.colorId] : [];
});

const collisionDisabledColorIds = computed(() => {
  const name = collisionColorToAssign.value;
  if (!name) return [];
  const key = collisionLabelKey(name);
  const current = collisionChoice(name);
  const collisionIds = new Set(colorCollisions.value.map((group) => group.colorId));
  const takenByAnotherName = new Set(
    colorSourceChoices.value
      .filter((item) => collisionLabelKey(item.sourceName) !== key)
      .map((item) => item.colorId)
  );
  const disabled = new Set<number>();
  for (const colorId of takenByAnotherName) disabled.add(colorId);
  for (const color of selectedColors.value) {
    const colorId = Number(color.id);
    if (current && colorId === current.colorId) continue;
    if (collisionIds.has(colorId) && !takenByAnotherName.has(colorId)) continue;
    disabled.add(colorId);
  }
  return [...disabled];
});

function openPendingColorPicker(sourceLabel: string) {
  collisionColorToAssign.value = null;
  pendingColorToLink.value = sourceLabel;
  pendingColorModalVisible.value = true;
}

function openCollisionColorPicker(sourceLabel: string) {
  pendingColorToLink.value = null;
  collisionColorToAssign.value = sourceLabel;
  pendingColorModalVisible.value = true;
}

function openPendingCategoryPicker(sourceLabel: string) {
  pendingCategoryToLink.value = sourceLabel;
  pendingCategoryModalVisible.value = true;
}

async function onPendingColorConfirm(colors: CouleurAPI[]) {
  const collisionLabel = collisionColorToAssign.value;
  const sourceLabel = pendingColorToLink.value;
  const official = colors[0];
  pendingColorModalVisible.value = false;
  pendingColorToLink.value = null;
  collisionColorToAssign.value = null;
  if (collisionLabel && official) {
    assignCollisionColor(collisionLabel, official);
    return;
  }
  if (!sourceLabel || !official || !editedProduct.value) return;
  const colorId = Number(official.id_couleur);
  if (!Number.isInteger(colorId) || colorId <= 0) {
    error("Couleur officielle invalide.");
    return;
  }
  try {
    await savePrestashopMapping("color", sourceLabel, colorId);
    patchEditedCatalogProduct({
      pendingColors: (editedProduct.value.pendingColors ?? []).filter(
        (label) => label !== sourceLabel
      ),
    });
    const alreadySelected = selectedColors.value.some(
      (color) => Number(color.id) === colorId
    );
    if (!alreadySelected) {
      selectedColors.value = [
        ...selectedColors.value,
        {
          id: colorId,
          nom: official.couleur_FR,
          isMain: selectedColors.value.length === 0,
          hexColor: official.hexColor || null,
          quantitesParTaille:
            form.soldBy === "melangees" && selectedSizeNames.value.length
              ? new Array(selectedSizeNames.value.length).fill(0)
              : undefined,
        },
      ];
    }
    syncColorStocks(true);
    await loadColorMappings();
    success(`« ${sourceLabel} » lié à « ${official.couleur_FR} ».`);
  } catch (caught) {
    error(
      caught instanceof Error
        ? caught.message
        : "Impossible d'enregistrer la liaison de couleur."
    );
  }
}

async function onPendingCategorySelect(path: CategoryPathSelection) {
  const sourceLabel = pendingCategoryToLink.value;
  pendingCategoryModalVisible.value = false;
  pendingCategoryToLink.value = null;
  if (!sourceLabel || !editedProduct.value) return;
  const categoryId = Number(path.leafId);
  if (!Number.isInteger(categoryId) || categoryId <= 0) {
    error("Catégorie eFashion invalide.");
    return;
  }
  if (String(categoryId).length !== 6) {
    error("Choisissez une catégorie feuille (6 chiffres).");
    return;
  }
  try {
    await savePrestashopMapping("category", sourceLabel, categoryId);
    form.categoryId = String(categoryId);
    restoreCategoryPath(form.categoryId);
    patchEditedCatalogProduct({
      pendingCategories: (editedProduct.value.pendingCategories ?? []).filter(
        (label) => label !== sourceLabel
      ),
      overrides: {
        ...(editedProduct.value.overrides || {}),
        categoryId,
      },
    });
    success(`« ${sourceLabel} » lié à la catégorie eFashion.`);
  } catch (caught) {
    error(
      caught instanceof Error
        ? caught.message
        : "Impossible d'enregistrer la liaison de catégorie."
    );
  }
}

function closeProduct() {
  if (!savingProduct.value) {
    productVariantStocks.value = [];
    productImageOverrides.value = [];
    pendingColorModalVisible.value = false;
    collisionColorToAssign.value = null;
    colorSourceChoices.value = [];
    pendingCategoryModalVisible.value = false;
    editedProduct.value = null;
  }
}

async function saveProduct() {
  if (!editedProduct.value) return;
  if (!canSaveProductForm.value) {
    error(
      `Complétez les champs obligatoires : ${productFormMissingFields.value.join(
        ", "
      )}.`
    );
    return;
  }
  const compositions = normalizedCompositionsForSave.value;
  const { valid, message } = validateCompositionsForSave(
    compositions,
    compositionType.value,
    compositionLocations.value
  );
  if (!valid) {
    error(message);
    return;
  }
  savingProduct.value = true;
  const productId = editedProduct.value.id;
  const savedPrice = parsePositivePrice(form.price);
  const savedWeight = parsePositiveWeight(form.weight);
  try {
    await savePrestashopProductOverrides(productId, {
      images: productImageOverrides.value,
      brandId: Number(form.brandId) || undefined,
      categoryId: Number(form.categoryId) || undefined,
      collectionId: Number(form.collectionId) || undefined,
      provenanceId: Number(form.provenanceId) || undefined,
      price: savedPrice,
      weight: savedWeight,
      dimensions: form.dimensions.trim() || undefined,
      compositionId: compositions[0]?.compositionId,
      compositionLocationId: compositions[0]?.locationId,
      compositionPercentage: Number(compositions[0]?.percentage) || undefined,
      compositions,
      colorId: selectedColors.value[0]?.id,
      colorIds: selectedColors.value.map((color) => color.id),
      colorSourceChoices: colorSourceChoices.value.map((choice) => ({
        sourceName: choice.sourceName,
        colorId: choice.colorId,
      })),
      colors: selectedColors.value.map((color) => ({
        colorId: color.id,
        isMain: color.isMain,
        quantitiesBySize: color.quantitesParTaille,
        sizeStocks: color.tailleStocks
          ?.filter((item) => item.quantity != null)
          .map((item) => ({
            size: item.taille,
            quantity: Number(item.quantity) || 0,
          })),
        stock: color.stock != null ? Number(color.stock) || 0 : undefined,
      })),
      soldBy:
        form.soldBy === "couleurs" ||
        form.soldBy === "melangees" ||
        form.soldBy === "tailles"
          ? form.soldBy
          : undefined,
      packId: Number(form.packId) || undefined,
      sizeRangeId: Number(form.sizeRangeId) || undefined,
      stock:
        form.soldBy === "melangees" && form.stock !== ""
          ? Math.max(0, Number(form.stock) || 0)
          : undefined,
    });
    if (savedPrice || savedWeight) {
      patchCatalogOverridesForProducts([productId], {
        ...(savedPrice ? { price: savedPrice } : {}),
        ...(savedWeight ? { weight: savedWeight } : {}),
      });
    }
    productImageOverrides.value = [];
    editedProduct.value = null;
    success("Informations MEL enregistrées.");
    await loadCatalog();
  } catch (caught) {
    error(
      caught instanceof Error ? caught.message : "Enregistrement impossible."
    );
  } finally {
    savingProduct.value = false;
  }
}

async function changePage(nextPage: number) {
  page.value = nextPage;
  await loadCatalog();
}

async function changePageSize(nextSize: number) {
  pageSize.value = nextSize;
  page.value = 1;
  await loadCatalog();
}

watch(
  () => [form.price, form.weight],
  () => {
    syncProductPrestaPriceHint();
  }
);

watch(
  () => form.packId,
  (packId, previousPackId) => {
    if (
      suppressPackUnitSync ||
      !editedProduct.value ||
      packId === previousPackId
    )
      return;
    if (!packId) return;
    applyPackUnitValues();
  },
  { flush: "sync" }
);

watch(
  [
    () => form.soldBy,
    () => form.sizeRangeId,
    () => selectedColors.value.map((color) => color.id).join(","),
  ],
  () => {
    if (!editedProduct.value) return;
    if (form.soldBy === "tailles") {
      form.packId = "";
    } else if (
      form.packId &&
      !packOptions.value.some(
        (item) => String(item.value) === String(form.packId)
      )
    ) {
      form.packId = "";
    }
    if (form.soldBy !== "melangees") {
      // stock paquet uniquement pour mélangées — on conserve la valeur saisie
      // jusqu'à l'enregistrement (ignorée si autre mode)
    }
    syncColorStocks(true);
  }
);

watch([() => bulkForm.soldBy, () => bulkForm.sizeRangeId], () => {
  if (bulkForm.soldBy === "tailles") {
    bulkForm.packId = "";
  } else if (
    bulkForm.packId &&
    !bulkPackOptions.value.some(
      (item) => String(item.value) === String(bulkForm.packId)
    )
  ) {
    bulkForm.packId = "";
  }
  syncBulkMelangeesColors();
});

watch(
  () => bulkSelectedColors.value.map((color) => color.id).join(","),
  () => {
    syncBulkMelangeesColors();
  }
);

function onSettingsSaved(
  settings: import("@/api/prestashop").PrestashopSettings
) {
  const initialSetup = !prestashopSettings.value.setupCompleted;
  prestashopSettings.value = settings;
  if (initialSetup && settings.setupCompleted) {
    showConfigurationModal.value = false;
    void loadCatalog();
  }
}

onMounted(async () => {
  try {
    connection.value = await getPrestashopConnection();
    if (connection.value.connected) {
      prestashopSettings.value = await getPrestashopSettings();
      await loadCatalog();
      if (!prestashopSettings.value.setupCompleted) {
        showConfigurationModal.value = true;
      }
    }
  } catch (caught) {
    error(
      caught instanceof Error
        ? caught.message
        : "Service PrestaShop indisponible."
    );
  } finally {
    initialLoading.value = false;
  }
});
</script>
